import type { Pathway, Lesson } from '../types';
import { SPARK_LESSONS } from './lessons/spark';
import { EXPLORER_LESSONS } from './lessons/explorer';
import { BUILDER_LESSONS } from './lessons/builder';
import { CHALLENGER_LESSONS } from './lessons/challenger';
import { SCIENCE_LIVING_THINGS_GAP_LESSONS } from './lessons/gap/science-living-things';
import { MATH_FRACTIONS_GAP_LESSONS } from './lessons/gap/math-fractions';
import { SCIENCE_CLIMATE_GAP_LESSONS } from './lessons/gap/science-climate';

// ── New STEAM pathways added for all tiers ───────────────────────────────────
const NEW_PATHWAYS: Pathway[] = [
  // SPARK — Science
  {
    id: 'science-living-things-4-6',
    title: 'Levende ting: Hva er egentlig i live?',
    titleEn: 'Living Things: What Is Actually Alive?',
    description: 'Hvordan vet vi at noe lever? Planter, dyr og til og med bakterier — alt liv deler hemmelige fellesnevnere.',
    descriptionEn: 'How do we know something is alive? Plants, animals and even bacteria share secret commonalities.',
    subject: 'science', strand: 'Biologi', ageBand: '4-7', phase: 'spark', level: 1,
    lessonIds: ['science-what-is-alive', 'science-plants-need', 'science-animals-move', 'science-lifecycles', 'science-habitats'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },
  // SPARK — Technology
  {
    id: 'tech-machines-everyday-4-6',
    title: 'Maskiner rundt oss: Hva hjelper oss?',
    titleEn: 'Machines Around Us: What Helps Us?',
    description: 'Fra hjulet til vaskemaskin — maskiner er overalt. Lær å se teknologi med nye øyne.',
    descriptionEn: 'From the wheel to the washing machine – machines are everywhere. Learn to see technology with new eyes.',
    subject: 'technology', strand: 'Teknologiforståelse', ageBand: '4-7', phase: 'spark', level: 1,
    lessonIds: ['tech-what-is-machine', 'tech-giving-instructions', 'tech-simple-robots', 'tech-buttons-screens', 'tech-make-a-machine'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },
  // SPARK — Engineering
  {
    id: 'eng-build-create-4-6',
    title: 'Bygg og skap: Ingeniører starter slik',
    titleEn: 'Build and Create: Engineers Start Like This',
    description: 'Ingeniører løser problemer ved å bygge ting. Du kan gjøre det samme — med papir, lim og nysgjerrighet.',
    descriptionEn: 'Engineers solve problems by building things. You can do the same – with paper, glue and curiosity.',
    subject: 'engineering', strand: 'Design og konstruksjon', ageBand: '4-7', phase: 'spark', level: 1,
    lessonIds: ['eng-strongest-shape', 'eng-design-thinking-spark', 'eng-build-tall', 'eng-bridges-simple', 'eng-invent-something'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },
  // SPARK — Arts
  {
    id: 'arts-colours-stories-4-6',
    title: 'Farger og fortellinger: Kunst starter her',
    titleEn: 'Colours and Stories: Art Starts Here',
    description: 'Farger har følelser, bilder forteller historier uten ord. Utforsk din egen kunstneriske stemme.',
    descriptionEn: 'Colours have feelings, images tell stories without words. Explore your own artistic voice.',
    subject: 'arts', strand: 'Visuell kunst', ageBand: '4-7', phase: 'spark', level: 1,
    lessonIds: ['arts-what-is-colour', 'arts-mixing-colours', 'arts-warm-cool', 'arts-my-story-drawing', 'arts-collage'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },

  // EXPLORER — Science
  {
    id: 'science-human-body-7-9',
    title: 'Menneskekroppen: Din indre supermaskine',
    titleEn: 'The Human Body: Your Inner Super Machine',
    description: 'Hjertet slår 100 000 ganger om dagen uten pause. Hjernen bruker 20% av all energi. Kroppen din er utrolig.',
    descriptionEn: 'The heart beats 100,000 times a day without a break. The brain uses 20% of all energy. Your body is incredible.',
    subject: 'science', strand: 'Biologi', ageBand: '8-12', phase: 'explorer', level: 1,
    lessonIds: ['science-heart-pump', 'science-digestion-journey', 'science-skeleton-muscles', 'science-brain-senses', 'science-immune-system'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },
  // EXPLORER — Technology
  {
    id: 'tech-coding-intro-7-9',
    title: 'Koding: Gi datamaskinen ordre',
    titleEn: 'Coding: Give the Computer Orders',
    description: 'Alle programmer er instruksjoner. Lær algoritmisk tenkning — den viktigste ferdigheten i det 21. århundre.',
    descriptionEn: 'All programs are instructions. Learn algorithmic thinking – the most important skill of the 21st century.',
    subject: 'technology', strand: 'Programmering', ageBand: '8-12', phase: 'explorer', level: 1,
    lessonIds: ['tech-algorithms-sandwich', 'tech-loops-patterns', 'tech-conditionals', 'tech-debug-find-bug', 'tech-mini-project'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },
  // EXPLORER — Engineering
  {
    id: 'eng-simple-machines-7-9',
    title: 'Enkle maskiner: Styrke uten muskler',
    titleEn: 'Simple Machines: Strength Without Muscles',
    description: 'En enkelt person kan flytte en stein på 10 tonn med en stang og et støttepunkt. Det er ikke magi — det er fysikk.',
    descriptionEn: 'A single person can move a 10-tonne stone with a lever and a fulcrum. It\'s not magic – it\'s physics.',
    subject: 'engineering', strand: 'Maskiner og mekanikk', ageBand: '8-12', phase: 'explorer', level: 1,
    lessonIds: ['eng-levers-playground', 'eng-wheels-axles', 'eng-pulleys-lift', 'eng-inclined-plane', 'eng-gears'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },
  // EXPLORER — Arts
  {
    id: 'arts-music-patterns-7-9',
    title: 'Musikk og mønstre: Matematikk du kan høre',
    titleEn: 'Music and Patterns: Mathematics You Can Hear',
    description: 'Rytme er brøk. Melodi er matematikk. Musikk er den eneste kunsten som eksisterer i tid — og den er full av mønstre.',
    descriptionEn: 'Rhythm is fractions. Melody is mathematics. Music is the only art that exists in time – and it\'s full of patterns.',
    subject: 'arts', strand: 'Musikk', ageBand: '8-12', phase: 'explorer', level: 1,
    lessonIds: ['arts-rhythm-fractions', 'arts-melody-pitch', 'arts-beat-pattern', 'arts-compose-4-bars', 'arts-instruments-families'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },

  // BUILDER — Science
  {
    id: 'science-evolution-10-12',
    title: 'Evolusjon: Livet tilpasser seg',
    titleEn: 'Evolution: Life Adapts',
    description: 'Isbjørner, kakerlakker og koraller — alle er resultater av millioner av år med prøving og feiling. Naturen har ingen plan — men den er genialt effektiv.',
    descriptionEn: 'Polar bears, cockroaches and corals – all results of millions of years of trial and error. Nature has no plan – but it is brilliantly efficient.',
    subject: 'science', strand: 'Evolusjon og genetikk', ageBand: '8-12', phase: 'builder', level: 1,
    lessonIds: ['science-natural-selection', 'science-adaptation', 'science-fossil-record', 'science-genetics-intro', 'science-extinction'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },
  // BUILDER — Technology
  {
    id: 'tech-internet-networks-10-12',
    title: 'Internett og nettverk: Verden i kabel',
    titleEn: 'Internet and Networks: The World in a Cable',
    description: 'Over 4 milliarder mennesker bruker internett. Det er bygget på enkle prinsipper — og du kan forstå dem.',
    descriptionEn: 'Over 4 billion people use the internet. It is built on simple principles – and you can understand them.',
    subject: 'technology', strand: 'Nettverk og datasystemer', ageBand: '8-12', phase: 'builder', level: 1,
    lessonIds: ['tech-how-internet-works', 'tech-binary-data', 'tech-encryption-basics', 'tech-databases', 'tech-cybersecurity'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },
  // BUILDER — Engineering
  {
    id: 'eng-structures-10-12',
    title: 'Konstruksjoner: Hva holder alt oppe?',
    titleEn: 'Structures: What Holds Everything Up?',
    description: 'Broer, skyskrapere, stadioner — alle er mesterverker av ingeniørkunst. Lær kreftene som holder dem oppe.',
    descriptionEn: 'Bridges, skyscrapers, stadiums – all masterpieces of engineering. Learn the forces that keep them standing.',
    subject: 'engineering', strand: 'Strukturer og materialer', ageBand: '8-12', phase: 'builder', level: 1,
    lessonIds: ['eng-bridges-forces', 'eng-materials-properties', 'eng-loads-reactions', 'eng-design-challenge', 'eng-real-world-structures'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },
  // BUILDER — Arts
  {
    id: 'arts-composition-10-12',
    title: 'Komposisjon: Kunsten å se',
    titleEn: 'Composition: The Art of Seeing',
    description: 'Hva gjør et bilde minneverdig? Ikke motivet — det er komposisjonen. Lær reglene de profesjonelle bruker.',
    descriptionEn: 'What makes an image memorable? Not the subject – it\'s the composition. Learn the rules professionals use.',
    subject: 'arts', strand: 'Komposisjon og visuell kunst', ageBand: '8-12', phase: 'builder', level: 1,
    lessonIds: ['arts-rule-of-thirds', 'arts-perspective-drawing', 'arts-light-shadow', 'arts-colour-harmony', 'arts-final-composition'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },

  // CHALLENGER — Science
  {
    id: 'science-climate-13-16',
    title: 'Klima og fremtid: Vitenskapen bak krisen',
    titleEn: 'Climate and Future: The Science Behind the Crisis',
    description: 'Klimaendring er ikke en mening — det er fysikk, kjemi og matematikk. Forstå mekanismene, evaluer løsningene.',
    descriptionEn: 'Climate change is not an opinion – it\'s physics, chemistry and mathematics. Understand the mechanisms, evaluate the solutions.',
    subject: 'science', strand: 'Klima og miljøvitenskap', ageBand: '13-16', phase: 'challenger', level: 1,
    lessonIds: ['science-greenhouse-effect', 'science-climate-models', 'science-ocean-acidification', 'science-tipping-points', 'science-solutions'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },
  // CHALLENGER — Technology
  {
    id: 'tech-ai-algorithms-13-16',
    title: 'AI og algoritmer: Fremtidens motor',
    titleEn: 'AI and Algorithms: The Engine of the Future',
    description: 'Maskinlæring, nevrale nettverk, bias i algoritmer — dette er verktøyene som omformer verden. Forstå dem nå.',
    descriptionEn: 'Machine learning, neural networks, algorithmic bias – these are the tools reshaping the world. Understand them now.',
    subject: 'technology', strand: 'Kunstig intelligens', ageBand: '13-16', phase: 'challenger', level: 1,
    lessonIds: ['tech-machine-learning', 'tech-neural-networks', 'tech-algorithmic-bias', 'tech-ai-ethics', 'tech-build-ai-project'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },
  // CHALLENGER — Engineering
  {
    id: 'eng-sustainable-design-13-16',
    title: 'Bærekraftig design: Ingeniørkunst for planeten',
    titleEn: 'Sustainable Design: Engineering for the Planet',
    description: 'Livsløpsanalyse, sirkulær økonomi, fornybar energi — fremtidens ingeniører løser ikke bare problemer, de forhindrer dem.',
    descriptionEn: 'Life cycle assessment, circular economy, renewable energy – tomorrow\'s engineers don\'t just solve problems, they prevent them.',
    subject: 'engineering', strand: 'Bærekraft og design', ageBand: '13-16', phase: 'challenger', level: 1,
    lessonIds: ['eng-lifecycle-analysis', 'eng-renewable-energy', 'eng-circular-economy', 'eng-green-materials', 'eng-sustainability-project'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },
  // CHALLENGER — Arts
  {
    id: 'arts-digital-creativity-13-16',
    title: 'Digital kreativitet: Kunst i det 21. århundret',
    titleEn: 'Digital Creativity: Art in the 21st Century',
    description: 'Generativ kunst, kreativ koding, digital design — kunsten og teknologien smelter sammen. Lær begge.',
    descriptionEn: 'Generative art, creative coding, digital design – art and technology are merging. Learn both.',
    subject: 'arts', strand: 'Digital kunst og medier', ageBand: '13-16', phase: 'challenger', level: 1,
    lessonIds: ['arts-digital-tools', 'arts-creative-coding', 'arts-animation', 'arts-sound-design', 'arts-digital-portfolio'],
    totalLessons: 5, estimatedWeeks: 2, isPrebuilt: true, generatedForChildId: null, createdAt: new Date(),
  },
];

export const PREBUILT_PATHWAYS: Pathway[] = [
  {
    id: 'math-fractions-8-12',
    title: 'Brøker: Fra pizza til Spotify',
    titleEn: 'Fractions: From pizza to Spotify',
    description: 'Oppdag at du allerede bruker brøk hver dag — og lær deg det matematiske språket for noe du allerede vet.',
    descriptionEn: 'Discover that you already use fractions every day — and learn the mathematical language for something you already know.',
    subject: 'mathematics',
    strand: 'Tall og operasjoner',
    ageBand: '8-12',
    phase: 'builder',
    level: 1,
    lessonIds: [
      'fractions-what-are-fractions',
      'fractions-equivalent',
      'fractions-adding-same',
      'fractions-comparing',
      'fractions-on-number-line',
    ],
    totalLessons: 5,
    estimatedWeeks: 2,
    isPrebuilt: true,
    generatedForChildId: null,
    createdAt: new Date(),
  },
  {
    id: 'math-geometry-8-12',
    title: 'Geometri: Formene som bygger verden',
    titleEn: 'Geometry: The shapes that build the world',
    description: 'Fra pyramidene i Egypt til honningkaker — geometri er overalt. Lær å måle, beregne og tegne den.',
    descriptionEn: 'From the pyramids of Egypt to honeycombs — geometry is everywhere. Learn to measure, calculate and draw it.',
    subject: 'mathematics',
    strand: 'Geometri',
    ageBand: '8-12',
    phase: 'builder',
    level: 1,
    lessonIds: [
      'geometry-shapes-intro',
      'geometry-perimeter',
      'geometry-area',
      'geometry-angles',
      'geometry-symmetry',
    ],
    totalLessons: 5,
    estimatedWeeks: 2,
    isPrebuilt: true,
    generatedForChildId: null,
    createdAt: new Date(),
  },
  {
    id: 'math-algebra-intro-8-12',
    title: 'Algebra: Når bokstaver erstatter tall',
    titleEn: 'Algebra: When letters replace numbers',
    description: 'Hva skjer når vi lar bokstaver representere tall vi ikke vet ennå? Oppdagelsen som forandret matematikken.',
    descriptionEn: "What happens when we let letters represent numbers we don't know yet? The discovery that changed mathematics.",
    subject: 'mathematics',
    strand: 'Algebra',
    ageBand: '8-12',
    phase: 'builder',
    level: 2,
    lessonIds: [
      'algebra-what-is-variable',
      'algebra-expressions',
      'algebra-equations-simple',
      'algebra-word-problems',
      'algebra-patterns',
    ],
    totalLessons: 5,
    estimatedWeeks: 2,
    isPrebuilt: true,
    generatedForChildId: null,
    createdAt: new Date(),
  },

  // ─── SPARK (4-6) ────────────────────────────────────────────────────────────
  {
    id: 'math-shapes-4-6',
    title: 'Former og mønstre: Hva er verden laget av?',
    titleEn: 'Shapes and Patterns: What Is the World Made Of?',
    description: 'Bier bygger perfekte sekskantet hus uten kalkulator. Snøfnugg har alltid seks armer. Naturen er full av matematikk — og du kan se den!',
    descriptionEn: 'Bees build perfect hexagonal homes without calculators. Snowflakes always have six arms. Nature is full of mathematics — and you can see it!',
    subject: 'mathematics',
    strand: 'Geometri',
    ageBand: '4-7',
    phase: 'spark',
    level: 1,
    lessonIds: [
      'shapes-why-hexagons',
      'shapes-circles-everywhere',
      'shapes-patterns-in-nature',
      'shapes-symmetry-butterflies',
      'shapes-building-with-shapes',
    ],
    totalLessons: 5,
    estimatedWeeks: 2,
    isPrebuilt: true,
    generatedForChildId: null,
    createdAt: new Date(),
  },

  // ─── EXPLORER (7-9) ──────────────────────────────────────────────────────────
  {
    id: 'math-multiplication-7-9',
    title: 'Ganging: Når tall vokser fort',
    titleEn: 'Multiplication: When Numbers Grow Fast',
    description: 'Hvis du bretter et papir 42 ganger, når det Månen. Men er det mulig? La oss utforske hva som skjer når tall dobler seg — igjen og igjen.',
    descriptionEn: 'If you fold a piece of paper 42 times, it reaches the Moon. But is that possible? Let us explore what happens when numbers double — again and again.',
    subject: 'mathematics',
    strand: 'Tall og operasjoner',
    ageBand: '8-12',
    phase: 'explorer',
    level: 1,
    lessonIds: [
      'multiplication-paper-moon',
      'multiplication-times-tables',
      'multiplication-area-model',
      'multiplication-word-problems',
      'multiplication-patterns',
    ],
    totalLessons: 5,
    estimatedWeeks: 2,
    isPrebuilt: true,
    generatedForChildId: null,
    createdAt: new Date(),
  },

  // ─── CHALLENGER (13-16) ───────────────────────────────────────────────────────
  {
    id: 'math-functions-13-16',
    title: 'Funksjoner: Ligningene som navigerer romskip',
    titleEn: 'Functions: The Equations That Navigate Spacecraft',
    description: 'NASA skjøt opp Voyager 1 i 1977 og vet fortsatt nøyaktig hvor den er — 23 milliarder km unna. Algebra er ikke symboler. Det er språket universet er skrevet i.',
    descriptionEn: 'NASA launched Voyager 1 in 1977 and still knows exactly where it is — 23 billion km away. Algebra is not symbols. It is the language the universe is written in.',
    subject: 'mathematics',
    strand: 'Algebra og funksjoner',
    ageBand: '13-16',
    phase: 'challenger',
    level: 1,
    lessonIds: [
      'functions-voyager-equation',
      'functions-slope-meaning',
      'functions-linear-vs-exponential',
      'functions-real-world-models',
      'functions-systems-of-equations',
    ],
    totalLessons: 5,
    estimatedWeeks: 2,
    isPrebuilt: true,
    generatedForChildId: null,
    createdAt: new Date(),
  },
  ...NEW_PATHWAYS,
];

export const SAMPLE_LESSONS: Record<string, Lesson> = {
  ...SPARK_LESSONS,
  ...EXPLORER_LESSONS,
  ...BUILDER_LESSONS,
  ...CHALLENGER_LESSONS,
  // Lessons 3-5 for three complete pathways — one per age band. Every pathway
  // declares five lessons; these are the first three to actually have all five,
  // so a child can finish a path instead of hitting a wall after lesson 2.
  ...SCIENCE_LIVING_THINGS_GAP_LESSONS,
  ...MATH_FRACTIONS_GAP_LESSONS,
  ...SCIENCE_CLIMATE_GAP_LESSONS,
  'fractions-what-are-fractions': {
    id: 'fractions-what-are-fractions',
    pathwayId: 'math-fractions-8-12',
    subject: 'mathematics',
    strand: 'Tall og operasjoner',
    ageBand: '8-12',
    phase: 'builder',
    level: 1,
    language: 'no',
    title: 'Hvorfor gir det å dele noe i flere biter deg mindre?',
    titleEn: 'Why does cutting something into more pieces give you less?',
    durationMinutes: 25,
    saganHook: {
      type: 'question',
      content: 'Tenk deg at du kutter en pizza i 3 deler og spiser 1. Du har spist én tredjedel. Men her er det merkelige: hvis du kutter den samme pizzaen i 100 deler og spiser 33 av dem, har du spist nesten nøyaktig det samme. Pizzaen ble ikke større. Men tallene ble helt annerledes. Hvordan er det mulig?',
    },
    saganHookEn: "Imagine cutting a pizza into 3 pieces and eating 1. You've eaten one third. But here's the strange part: if you cut the same pizza into 100 pieces and eat 33 of them, you've eaten almost exactly the same amount. The pizza didn't get bigger. But the numbers changed completely. How is that possible?",
    realWorldBridgeEn: {
      context: 'Spotify, phone battery, cooking',
      content: "You already use fractions every day without knowing it. When Spotify says a song is 3 minutes and 30 seconds long, those 30 seconds are half a minute — written as ½. When your phone is at 75%, that's three quarters — ¾. You've been using fractions your whole life. Today you learn the language for something you already know.",
    },
    coreConceptEn: {
      definition: 'A fraction represents a part of a whole. It is written as one number over another: numerator / denominator.',
      terminology: ['numerator', 'denominator', 'fraction bar', 'proper fraction'],
      explanation: 'The denominator (number below the bar) tells us how many equal parts the whole is divided into. The numerator (number above the bar) tells us how many of those parts we are talking about. If you divide a pizza into 4 equal parts and take 3, you have ¾ of the pizza. Denominator = 4 (four parts total). Numerator = 3 (three parts you have).',
      workedExamples: [
        {
          problem: 'You have a chocolate bar with 8 pieces. You eat 3. What fraction did you eat?',
          solution: '3/8',
          explanation: 'The chocolate is divided into 8 equal pieces (denominator = 8). You eat 3 of them (numerator = 3). The answer is 3/8 — three eighths.',
        },
        {
          problem: 'A class has 20 students. 12 of them are girls. What fraction are girls?',
          solution: '12/20 = 3/5',
          explanation: 'There are 20 students total (denominator = 20). 12 are girls (numerator = 12). The fraction 12/20 can be simplified to 3/5 because 12 and 20 are both divisible by 4.',
        },
      ],
    },
    practiceQuestionsEn: [
      {
        question: 'An orange is divided into 6 equal pieces. You eat 2 of them. What fraction did you eat?',
        type: 'multiple_choice',
        options: ['2/6', '6/2', '4/6', '1/3'],
        correctAnswer: '2/6',
        explanation: 'You have 6 pieces total (denominator) and ate 2 (numerator). The answer is 2/6. (2/6 is the same as 1/3, but we accept both!)',
        solutionSteps: [
          '🍊 Remember: a fraction is written as numerator / denominator',
          'The orange is divided into 6 equal pieces → denominator = 6',
          'You ate 2 of the pieces → numerator = 2',
          'Put it together: 2 / 6 = 2/6 ✓',
        ],
      },
      {
        question: 'What is the denominator in the fraction 5/8?',
        type: 'multiple_choice',
        options: ['5', '8', '13', '3'],
        correctAnswer: '8',
        explanation: 'The denominator is always the number below the fraction bar. In 5/8, the denominator is 8 — meaning the whole is divided into 8 equal parts.',
        solutionSteps: [
          '🔢 Look at the fraction: 5 / 8',
          'The fraction bar divides the numbers: numerator (top) and denominator (bottom)',
          'The denominator = the number BELOW the bar',
          'Below the bar in 5/8 is: 8 ✓',
        ],
      },
      {
        question: 'An hour has 60 minutes. You spent 15 minutes on homework. What fraction of the hour is used?',
        type: 'multiple_choice',
        options: ['15/60', '60/15', '45/60', '1/3'],
        correctAnswer: '15/60',
        explanation: '15 minutes out of 60 total is 15/60. This is the same as ¼ (a quarter hour), but 15/60 is the direct answer.',
        solutionSteps: [
          '⏱ A full hour = 60 minutes. That is the TOTAL → denominator = 60',
          'You spent 15 minutes. That is YOUR PART → numerator = 15',
          'Put it together: your part / total = 15 / 60',
          "The answer is 15/60 ✓ (that's the same as ¼ hour!)",
        ],
      },
      {
        question: 'What is the numerator in the fraction 7/9?',
        type: 'multiple_choice',
        options: ['9', '7', '2', '16'],
        correctAnswer: '7',
        explanation: 'The numerator is always the number ABOVE the fraction bar. In 7/9, the numerator is 7.',
        solutionSteps: [
          '🔢 Look at the fraction: 7 / 9',
          'The numerator = the number ABOVE the bar',
          'Above the bar in 7/9: 7',
          'The answer is 7 ✓',
        ],
      },
      {
        question: 'A pizza is divided into 8 equal slices. You eat 5 of them. What fraction did you eat?',
        type: 'multiple_choice',
        options: ['5/8', '8/5', '3/8', '1/5'],
        correctAnswer: '5/8',
        explanation: 'You ate 5 out of 8 slices. Numerator = 5, denominator = 8. The answer is 5/8.',
        solutionSteps: [
          '🍕 Total number of slices → denominator = 8',
          'Number of slices you ate → numerator = 5',
          'Put it together: 5 / 8 = 5/8 ✓',
        ],
      },
      {
        question: 'A class has 30 students. 10 are absent. What fraction of the class is absent?',
        type: 'multiple_choice',
        options: ['10/30', '20/30', '30/10', '10/20'],
        correctAnswer: '10/30',
        explanation: '10 students out of 30 total are absent. The fraction is 10/30 (which is the same as 1/3).',
        solutionSteps: [
          '👥 Total number of students → denominator = 30',
          'Number absent → numerator = 10',
          'The fraction is: 10/30 ✓',
        ],
      },
      {
        question: 'Which fraction is bigger: 1/2 or 1/4?',
        type: 'multiple_choice',
        options: ['1/2', '1/4', 'They are equal', 'Impossible to say'],
        correctAnswer: '1/2',
        explanation: 'The larger the denominator, the smaller each piece. 1/2 means one of two parts, 1/4 means one of four parts. A half is bigger than a quarter.',
        solutionSteps: [
          '🍰 Imagine one cake cut into 2 parts — each half is large',
          'Same cake cut into 4 parts — each quarter is much smaller',
          'Larger denominator → smaller pieces',
          '1/2 is bigger than 1/4 ✓',
        ],
      },
      {
        question: 'A chocolate bar has 12 pieces. You give away 4. What fraction is left?',
        type: 'multiple_choice',
        options: ['8/12', '4/12', '4/8', '12/4'],
        correctAnswer: '8/12',
        explanation: 'You gave away 4, so 12 - 4 = 8 pieces remain. The fraction left is 8/12.',
        solutionSteps: [
          '🍫 Start: 12 pieces total → denominator = 12',
          'You gave away 4 pieces',
          'Remaining: 12 - 4 = 8 pieces → numerator = 8',
          'Fraction left: 8/12 ✓',
        ],
      },
      {
        question: 'What does the numerator 3 in the fraction 3/4 represent?',
        type: 'multiple_choice',
        options: ['The number of parts you have', 'The total number of parts', 'The size of the whole', 'Nothing important'],
        correctAnswer: 'The number of parts you have',
        explanation: 'The numerator tells us how many parts we are talking about. In 3/4, the numerator 3 means we have 3 of the parts.',
        solutionSteps: [
          '📊 The fraction 3/4 means: 3 parts out of 4 total',
          'Denominator (4) = total number of equal parts',
          'Numerator (3) = the number of parts YOU HAVE',
          'The numerator 3 = the number of parts you have ✓',
        ],
      },
      {
        question: 'You fold a piece of paper into 5 equal parts and colour 2. What fraction is coloured?',
        type: 'multiple_choice',
        options: ['2/5', '5/2', '3/5', '2/3'],
        correctAnswer: '2/5',
        explanation: 'The paper is divided into 5 equal parts (denominator = 5). You coloured 2 of them (numerator = 2). The answer is 2/5.',
        solutionSteps: [
          '📄 Fold the paper into 5 equal parts → denominator = 5',
          'Colour 2 of the parts → numerator = 2',
          'Fraction coloured: 2/5 ✓',
        ],
      },
    ],
    handsOnActivityEn: {
      title: 'Paper folding — Make your own fractions',
      materials: ['3–4 equal sheets of A4 paper', 'pencil', 'ruler'],
      instructions: [
        'Take the first sheet and fold it into 2 equal parts. Open it again. Colour one part. What is the fraction? (1/2)',
        'Take the second sheet and fold it into 4 equal parts. Open it again. Colour 3 of the parts. What is the fraction? (3/4)',
        'Fold the third sheet into 8 equal parts. Colour 5. What is the fraction? (5/8)',
        'Question: If you colour half of the 8-part sheet, is it the same as the 1/2 fraction from the first sheet? Count and see!',
      ],
      timeMinutes: 10,
      whatToLookFor: 'See if the child discovers that different fractions can represent the same amount (equivalent fractions). This is the next lesson!',
    },
    openDoorEn: 'You now know what a fraction is. But here is a question for next time: ½ and 2/4 look like different numbers — but they are the same. And 3/6 and 1/2 are also the same. Are there infinitely many fractions that all mean exactly the same thing? The answer is yes. And that is both strange and beautiful.',
    learningObjectivesEn: [
      'Understand that a fraction represents a part of a whole',
      'Identify numerator and denominator',
      'Write fractions from everyday situations',
    ],
    parentGuideEn: {
      whatChildShouldKnow: 'Your child should now understand that a fraction represents a part of a whole, what numerator and denominator mean, and that they already use fractions in everyday life.',
      commonMisconceptions: 'Many children think that the larger the denominator, the larger the fraction. Help them see that 1/8 is actually smaller than 1/2 — the more pieces you divide something into, the smaller each piece is.',
      dinnerTableQuestion: 'Ask your child: "Which is bigger — half a pizza or three eighths of a pizza? And how do you know?"',
      nextLessonPreview: 'Next time: What are equivalent fractions? And why are ½, 2/4 and 4/8 actually the same — even though the numbers look completely different?',
    },
    realWorldBridge: {
      context: 'Spotify, telefonbatteri, matlaging',
      content: 'Du bruker allerede brøk hver dag uten å vite det. Når Spotify sier at en sang er 3 minutter og 30 sekunder lang, er de 30 sekundene en halvt minutt — skrevet som ½. Når telefonen din er på 75%, er det tre fjerdedeler — ¾. Du har brukt brøk hele livet. I dag lærer du språket for noe du allerede kan.',
    },
    coreConcept: {
      definition: 'En brøk representerer en del av en helhet. Den skrives som ett tall over et annet: teller / nevner.',
      terminology: ['teller', 'nevner', 'brøkstrek', 'ekte brøk'],
      explanation: 'Nevneren (tallet under streken) forteller oss hvor mange like deler helheten er delt inn i. Telleren (tallet over streken) forteller oss hvor mange av disse delene vi snakker om. Hvis du deler en pizza i 4 like deler og tar 3, har du ¾ av pizzaen. Nevner = 4 (fire deler totalt). Teller = 3 (tre deler du har).',
      workedExamples: [
        {
          problem: 'Du har en sjokolade med 8 biter. Du spiser 3. Hvilken brøk har du spist?',
          solution: '3/8',
          explanation: 'Sjokoladen er delt i 8 like biter (nevner = 8). Du spiser 3 av dem (teller = 3). Svaret er 3/8 — tre åttedeler.',
        },
        {
          problem: 'En klasse har 20 elever. 12 av dem er jenter. Hvilken brøk er jenter?',
          solution: '12/20 = 3/5',
          explanation: 'Det er 20 elever totalt (nevner = 20). 12 er jenter (teller = 12). Brøken 12/20 kan forenkles til 3/5 fordi 12 og 20 begge er delelige med 4.',
        },
      ],
    },
    interactiveComponent: {
      type: 'slider',
      description: 'Dra glidebryteren for å dele en sirkel i ulike antall like deler. Se hvordan nevneren endres og hva én del (1/n) ser ut som.',
      phetUrl: null,
    },
    practiceQuestions: [
      {
        question: 'En appelsin er delt i 6 like deler. Du spiser 2 av dem. Hvilken brøk har du spist?',
        type: 'multiple_choice',
        options: ['2/6', '6/2', '4/6', '1/3'],
        correctAnswer: '2/6',
        explanation: 'Du har 6 deler totalt (nevner) og spiste 2 (teller). Svaret er 2/6. (2/6 er det samme som 1/3, men vi godtar begge svar!)',
        solutionSteps: [
          '🍊 Husk: en brøk skrives som teller / nevner',
          'Appelsin er delt i 6 like deler → nevner = 6',
          'Du spiste 2 av delene → teller = 2',
          'Sett det sammen: 2 / 6 = 2/6 ✓',
        ],
      },
      {
        question: 'Hva er nevneren i brøken 5/8?',
        type: 'multiple_choice',
        options: ['5', '8', '13', '3'],
        correctAnswer: '8',
        explanation: 'Nevneren er alltid tallet under brøkstreken. I 5/8 er nevneren 8 — det betyr at helheten er delt i 8 like deler.',
        solutionSteps: [
          '🔢 Se på brøken: 5 / 8',
          'Brøkstreken deler tallene i to: teller (over) og nevner (under)',
          'Nevneren = tallet UNDER streken',
          'Under streken i 5/8 er: 8 ✓',
        ],
      },
      {
        question: 'En time har 60 minutter. Du har brukt 15 minutter på lekser. Hvilken brøk av timen er brukt?',
        type: 'multiple_choice',
        options: ['15/60', '60/15', '45/60', '1/3'],
        correctAnswer: '15/60',
        explanation: '15 minutter av 60 totalt er 15/60. Dette er det samme som ¼ (en kvart time), men 15/60 er det direkte svaret.',
        solutionSteps: [
          '⏱ En hel time = 60 minutter. Det er det TOTALE → nevner = 60',
          'Du brukte 15 minutter. Det er DIN DEL → teller = 15',
          'Sett det sammen: din del / totalt = 15 / 60',
          'Svaret er 15/60 ✓ (det er det samme som ¼ time!)',
        ],
      },
      {
        question: 'Hva er telleren i brøken 7/9?',
        type: 'multiple_choice',
        options: ['9', '7', '2', '16'],
        correctAnswer: '7',
        explanation: 'Telleren er alltid tallet OVER brøkstreken. I 7/9 er telleren 7.',
        solutionSteps: [
          '🔢 Se på brøken: 7 / 9',
          'Telleren = tallet OVER streken',
          'Over streken i 7/9 er: 7',
          'Svaret er 7 ✓',
        ],
      },
      {
        question: 'En pizza er delt i 8 like stykker. Du spiser 5 av dem. Hvilken brøk har du spist?',
        type: 'multiple_choice',
        options: ['5/8', '8/5', '3/8', '1/5'],
        correctAnswer: '5/8',
        explanation: 'Du spiste 5 av 8 stykker. Teller = 5, nevner = 8. Svaret er 5/8.',
        solutionSteps: [
          '🍕 Totalt antall stykker → nevner = 8',
          'Antall stykker du spiste → teller = 5',
          'Sett det sammen: 5 / 8 = 5/8 ✓',
        ],
      },
      {
        question: 'En klasse har 30 elever. 10 er borte. Hvilken brøk av klassen er borte?',
        type: 'multiple_choice',
        options: ['10/30', '20/30', '30/10', '10/20'],
        correctAnswer: '10/30',
        explanation: '10 elever av totalt 30 er borte. Brøken er 10/30 (som er det samme som 1/3).',
        solutionSteps: [
          '👥 Totalt antall elever → nevner = 30',
          'Antall som er borte → teller = 10',
          'Brøken er: 10/30 ✓',
        ],
      },
      {
        question: 'Hvilken brøk er størst: 1/2 eller 1/4?',
        type: 'multiple_choice',
        options: ['1/2', '1/4', 'De er like store', 'Umulig å si'],
        correctAnswer: '1/2',
        explanation: 'Jo større nevneren er, jo mindre er hver del. 1/2 betyr én av to deler, 1/4 betyr én av fire deler. En halv er større enn en fjerdedel.',
        solutionSteps: [
          '🍰 Forestill deg én kake delt i 2 deler — halvparten er stor',
          'Forestill deg samme kake delt i 4 deler — en firedel er mye mindre',
          'Jo større nevner → jo mindre biter',
          '1/2 er større enn 1/4 ✓',
        ],
      },
      {
        question: 'En sjokolade har 12 biter. Du gir bort 4. Hvilken brøk er igjen?',
        type: 'multiple_choice',
        options: ['8/12', '4/12', '4/8', '12/4'],
        correctAnswer: '8/12',
        explanation: 'Du ga bort 4, så 12 - 4 = 8 biter er igjen. Brøken som er igjen er 8/12.',
        solutionSteps: [
          '🍫 Start: 12 biter totalt → nevner = 12',
          'Du ga bort 4 biter',
          'Igjen: 12 - 4 = 8 biter → teller = 8',
          'Brøk igjen: 8/12 ✓',
        ],
      },
      {
        question: 'Hva representerer telleren 3 i brøken 3/4?',
        type: 'multiple_choice',
        options: ['Antall deler du har', 'Totalt antall deler', 'Størrelsen på helheten', 'Ingenting viktig'],
        correctAnswer: 'Antall deler du har',
        explanation: 'Telleren forteller oss hvor mange deler vi snakker om. I 3/4 betyr telleren 3 at vi har 3 av delene.',
        solutionSteps: [
          '📊 Brøken 3/4 betyr: 3 deler av totalt 4',
          'Nevner (4) = totalt antall like deler',
          'Teller (3) = antall deler DU HAR',
          'Telleren 3 = antall deler du har ✓',
        ],
      },
      {
        question: 'Du bretter et papir i 5 like deler og farger 2. Hvilken brøk er fargelagt?',
        type: 'multiple_choice',
        options: ['2/5', '5/2', '3/5', '2/3'],
        correctAnswer: '2/5',
        explanation: 'Papiret er delt i 5 like deler (nevner = 5). Du farget 2 av dem (teller = 2). Svaret er 2/5.',
        solutionSteps: [
          '📄 Bretter papiret i 5 like deler → nevner = 5',
          'Farger 2 av delene → teller = 2',
          'Brøk fargelagt: 2/5 ✓',
        ],
      },
    ],
    handsOnActivity: {
      title: 'Papirbretting — Lag dine egne brøker',
      materials: ['3–4 like ark med A4-papir', 'blyant', 'linjal'],
      instructions: [
        'Ta det første arket og brett det i 2 like deler. Åpne det igjen. Farg én del. Hva er brøken? (1/2)',
        'Ta det andre arket og brett det i 4 like deler. Åpne det igjen. Farg 3 av delene. Hva er brøken? (3/4)',
        'Brett det tredje arket i 8 like deler. Farg 5. Hva er brøken? (5/8)',
        'Spørsmål: Hvis du farger halvparten av 8-delt arket, er det det samme som 1/2-brøken fra det første arket? Tell og se!',
      ],
      timeMinutes: 10,
      whatToLookFor: 'Se om barnet oppdager at ulike brøker kan representere den samme mengden (ekvivalente brøker). Dette er neste leksjon!',
    },
    printableWorksheet: {
      title: 'Brøk-utforskning: Din første brøkside',
      sections: [
        {
          type: 'warmup',
          title: 'Oppvarming',
          instructions: 'Se på disse formene. Hvor stor del er skyggelagt?',
          content: '1. En sirkel delt i 4 deler, 1 skyggelagt: ___/___\n2. En rektangel delt i 6 deler, 2 skyggelagt: ___/___\n3. En trekant delt i 3 deler, 3 skyggelagt: ___/___',
        },
        {
          type: 'practice',
          title: '5 øvingsspørsmål',
          instructions: 'Sett ring rundt riktig svar:',
          content: '1. En appelsin er delt i 6 like deler. Du spiser 2. Brøken du spiste er:\n   a) 6/2   b) 2/6   c) 4/6   d) 1/4\n\n2. Hva er nevneren i 5/8?\n   a) 5   b) 13   c) 8   d) 3\n\n3. Hva er telleren i 7/9?\n   a) 9   b) 16   c) 7   d) 2\n\n4. En pizza er delt i 8 stykker. Du spiser 5. Brøken spist er:\n   a) 8/5   b) 3/8   c) 1/5   d) 5/8\n\n5. Hvilken brøk er størst?\n   a) 1/4   b) De er like   c) 1/2   d) Umulig å si',
        },
        {
          type: 'challenge',
          title: 'Utfordring',
          instructions: 'Løs dette problemet:',
          content: 'Sara, Per og Emma deler en kake likt. Mamma tar også én del til kaffen. Kaken er delt i 8 like stykker. Alle fire tar ett stykke hver. Hvilken brøk av kaken er spist? Hvilken brøk er igjen?',
        },
        {
          type: 'reflection',
          title: 'Refleksjon',
          instructions: 'Skriv med egne ord:',
          content: 'Hva er det merkeligste eller mest overraskende du lærte om brøk i dag?\n\n_____________________________________________________________\n_____________________________________________________________',
        },
      ],
    },
    printableWorksheetEn: {
      title: 'Fraction exploration: Your first fraction page',
      sections: [
        {
          type: 'warmup',
          title: 'Warm-up',
          instructions: 'Look at these shapes. What fraction is shaded?',
          content: '1. A circle divided into 4 parts, 1 shaded: ___/___\n2. A rectangle divided into 6 parts, 2 shaded: ___/___\n3. A triangle divided into 3 parts, 3 shaded: ___/___',
        },
        {
          type: 'practice',
          title: '5 practice questions',
          instructions: 'Circle the correct answer:',
          content: '1. An orange is divided into 6 equal pieces. You eat 2. The fraction you ate is:\n   a) 6/2   b) 2/6   c) 4/6   d) 1/4\n\n2. What is the denominator in 5/8?\n   a) 5   b) 13   c) 8   d) 3\n\n3. What is the numerator in 7/9?\n   a) 9   b) 16   c) 7   d) 2\n\n4. A pizza is divided into 8 slices. You eat 5. The fraction eaten is:\n   a) 8/5   b) 3/8   c) 1/5   d) 5/8\n\n5. Which fraction is bigger?\n   a) 1/4   b) They are equal   c) 1/2   d) Impossible to say',
        },
        {
          type: 'challenge',
          title: 'Challenge',
          instructions: 'Solve this problem:',
          content: 'Sara, Per and Emma share a cake equally. Mum also takes one piece with her coffee. The cake is cut into 8 equal slices. All four take one slice each. What fraction of the cake is eaten? What fraction is left?',
        },
        {
          type: 'reflection',
          title: 'Reflection',
          instructions: 'Write in your own words:',
          content: 'What is the strangest or most surprising thing you learned about fractions today?\n\n_____________________________________________________________\n_____________________________________________________________',
        },
      ],
    },
    parentGuide: {
      whatChildShouldKnow: 'Barnet ditt bør nå forstå at en brøk representerer en del av en helhet, hva teller og nevner betyr, og at de allerede bruker brøk i hverdagen.',
      commonMisconceptions: 'Mange barn tror at jo større nevneren er, jo større er brøken. Hjelp dem å se at 1/8 faktisk er mindre enn 1/2 — jo flere deler du deler noe i, jo mindre er hver del.',
      dinnerTableQuestion: 'Spør barnet ditt: "Hvilken er størst — en halv pizza eller tre åttedeler pizza? Og hvordan vet du det?"',
      nextLessonPreview: 'Neste gang: Hva er ekvivalente brøker? Og hvorfor er ½, 2/4 og 4/8 egentlig det samme — selv om tallene ser helt forskjellige ut?',
    },
    openDoor: 'Du vet nå hva en brøk er. Men her er et spørsmål for neste gang: ½ og 2/4 ser ut som forskjellige tall — men de er det samme. Og 3/6 og 1/2 er også det samme. Finnes det uendelig mange brøker som alle betyr nøyaktig det samme? Svaret er ja. Og det er både merkelig og nydelig.',
    curriculumSources: ['NCERT Grade 4 Ch. 9 — Fractions', 'NDLA Matematikk 5. trinn — Brøk'],
    learningObjectives: [
      'Forstå at en brøk representerer en del av en helhet',
      'Identifisere teller og nevner',
      'Skrive brøker fra hverdagssituasjoner',
    ],
    generatedAt: new Date(),
    version: 1,
  },

  'geometry-shapes-intro': {
    id: 'geometry-shapes-intro',
    pathwayId: 'math-geometry-8-12',
    subject: 'mathematics',
    strand: 'Geometri',
    ageBand: '8-12',
    phase: 'builder',
    level: 1,
    language: 'no',
    title: 'Hvordan bygde egypterne pyramidene med bare en pinne og en skygge?',
    titleEn: 'How did the Egyptians build the pyramids with just a stick and a shadow?',
    durationMinutes: 25,
    saganHook: {
      type: 'question',
      content: 'For over 2500 år siden målte en gresk matematiker høyden på den store pyramiden i Giza — uten å klatre opp på den, uten laser, uten moderne instrumenter. Han brukte bare en pinne, sin egen skygge, og en enkel geometrisk idé. Hvordan? Og hva har det med formene du tegner i dag å gjøre?',
    },
    saganHookEn: 'Over 2,500 years ago, a Greek mathematician measured the height of the Great Pyramid at Giza — without climbing it, without lasers, without any modern tools. He used only a stick, his own shadow, and one simple geometric idea. How? And what does it have to do with the shapes you draw today?',
    realWorldBridgeEn: {
      context: 'Architecture, nature, sports',
      content: "Shapes are all around you. A football pitch is a rectangle. Stop signs are octagons. Honeycombs are hexagons — and that's no accident. Bees chose the hexagon because it uses the least wax to store the most honey. Nature solves geometric optimisation problems. You do the same.",
    },
    coreConceptEn: {
      definition: 'A polygon is a closed, flat figure with straight sides. The number of sides determines its name.',
      terminology: ['polygon', 'triangle', 'square', 'rectangle', 'pentagon', 'hexagon', 'vertices', 'sides'],
      explanation: 'We classify polygons by the number of sides: 3 sides = triangle, 4 sides = quadrilateral, 5 sides = pentagon, 6 sides = hexagon. A regular polygon has all sides and angles equal. A triangle with all sides equal is called an equilateral triangle.',
      workedExamples: [
        {
          problem: 'What is a polygon with 8 equal sides called?',
          solution: 'Octagon (regular octagon)',
          explanation: 'Octa- means eight in Greek. Stop signs are a well-known example of an (almost regular) octagon.',
        },
      ],
    },
    practiceQuestionsEn: [
      {
        question: 'A stop sign has 8 sides. What is this shape called?',
        type: 'multiple_choice',
        options: ['Hexagon', 'Octagon', 'Pentagon', 'Decagon'],
        correctAnswer: 'Octagon',
        explanation: 'Octa = eight. A polygon with 8 sides is an octagon.',
        solutionSteps: [
          '🛑 Count the sides on a stop sign: it has 8 sides',
          'Greek prefix for 8 = octa (think: octopus = 8 arms!)',
          'A polygon with 8 sides = octa + gon',
          'The answer is Octagon ✓',
        ],
      },
      {
        question: 'Which polygon has 6 sides?',
        type: 'multiple_choice',
        options: ['Pentagon', 'Heptagon', 'Hexagon', 'Octagon'],
        correctAnswer: 'Hexagon',
        explanation: 'Hexa = six in Greek. A hexagon has 6 sides — like a honeycomb cell.',
        solutionSteps: [
          '🐝 Think of a honeycomb cell — it has 6 sides',
          'Greek prefix for 6 = hexa',
          'Hexa + gon = Hexagon',
          'A hexagon has 6 sides ✓',
        ],
      },
      {
        question: 'A regular polygon has all sides equal. Is a rectangle (that is not a square) a regular polygon?',
        type: 'multiple_choice',
        options: ['Yes', 'No', 'Sometimes', 'Impossible to say'],
        correctAnswer: 'No',
        explanation: 'A rectangle has 4 right angles, but its sides are not all equal in length. Only a square is a regular quadrilateral.',
        solutionSteps: [
          '📐 Regular polygon = all sides EQUAL LENGTH and all angles equal',
          'A rectangle has 2 long sides and 2 short sides',
          'Are all the sides equal in length? NO — they are different',
          'Conclusion: No, a rectangle is not regular ✓',
        ],
      },
      {
        question: 'How many sides does a triangle have?',
        type: 'multiple_choice',
        options: ['2', '3', '4', '5'],
        correctAnswer: '3',
        explanation: 'A triangle is a polygon with exactly 3 sides and 3 vertices. "Tri" means three.',
        solutionSteps: [
          '📐 "Triangle" contains the word "tri"',
          'Tri → 3 sides',
          'A triangle has 3 sides and 3 vertices',
          'The answer is 3 ✓',
        ],
      },
      {
        question: 'What do we call the corner points of a polygon?',
        type: 'multiple_choice',
        options: ['Sides', 'Edges', 'Vertices', 'Diagonals'],
        correctAnswer: 'Vertices',
        explanation: 'Vertices (singular: vertex) are the points where the sides of a polygon meet.',
        solutionSteps: [
          '📍 In a polygon, the sides meet at points',
          'These points are called vertices (singular: vertex)',
          'Each corner of a polygon is a vertex',
          'The answer is Vertices ✓',
        ],
      },
      {
        question: 'How many sides does a pentagon have?',
        type: 'multiple_choice',
        options: ['4', '5', '6', '7'],
        correctAnswer: '5',
        explanation: 'Penta- means five in Greek. A pentagon has 5 sides.',
        solutionSteps: [
          '🏛 Penta = five in Greek',
          'Penta + gon = Pentagon',
          'A pentagon has 5 sides ✓',
        ],
      },
      {
        question: 'How many right angles (90°) does a square have?',
        type: 'multiple_choice',
        options: ['1', '2', '3', '4'],
        correctAnswer: '4',
        explanation: 'A square has 4 corners and each corner is a right angle of 90°. Total: 4 right angles.',
        solutionSteps: [
          '⬛ A square is a four-sided shape',
          'Each corner of a square is 90°',
          'Number of corners: 4',
          'Number of right angles: 4 ✓',
        ],
      },
      {
        question: 'What is a polygon with 10 sides called?',
        type: 'multiple_choice',
        options: ['Nonagon', 'Decagon', 'Hexagon', 'Octagon'],
        correctAnswer: 'Decagon',
        explanation: 'Deca- means ten in Greek. A polygon with 10 sides is a decagon.',
        solutionSteps: [
          '🔟 Deca = ten in Greek (think: decade = 10 years)',
          'Deca + gon = Decagon',
          'A polygon with 10 sides is called a decagon ✓',
        ],
      },
      {
        question: 'Bees use this shape in their honeycombs because it is most efficient. Which shape is it?',
        type: 'multiple_choice',
        options: ['Triangle', 'Square', 'Hexagon', 'Circle'],
        correctAnswer: 'Hexagon',
        explanation: 'Bees use hexagons in their honeycombs because this shape uses the least wax to store the most honey.',
        solutionSteps: [
          '🍯 Think of a honeycomb pattern',
          'Each cell is shaped like a six-sided figure',
          'Six sides = Hexagon',
          'The answer is Hexagon ✓',
        ],
      },
      {
        question: 'A regular polygon has all sides...',
        type: 'multiple_choice',
        options: ['Equal in length', 'Different lengths', 'Curved', 'Parallel'],
        correctAnswer: 'Equal in length',
        explanation: 'A regular polygon has all sides equal in length AND all angles equal. Example: a square is a regular quadrilateral.',
        solutionSteps: [
          '📏 "Regular" polygon means it is perfectly symmetrical',
          'All sides = equal length',
          'All angles = equal',
          'The answer is: Equal in length ✓',
        ],
      },
    ],
    handsOnActivityEn: {
      title: 'Polygon hunt — Find shapes in your home',
      materials: ['Paper and pencil', 'Ruler'],
      instructions: [
        'Walk around your home. Find 2 examples of each of these shapes: triangle, quadrilateral, hexagon.',
        'Draw them or write down what they were.',
        'Challenge: Find a shape with more than 8 sides somewhere in your home.',
      ],
      timeMinutes: 10,
      whatToLookFor: 'The child should start to "see" geometry in everyday objects — tiles, windows, patterns on fabric.',
    },
    openDoorEn: 'You can now identify polygons. But what happens inside a polygon? What is the sum of all the angles in a triangle? In a quadrilateral? Is there a pattern? This is actually one of the most beautiful discoveries in geometry.',
    learningObjectivesEn: [
      'Identify and name common polygons',
      'Understand the relationship between number of sides and polygon name',
      'Distinguish between regular and irregular polygons',
    ],
    parentGuideEn: {
      whatChildShouldKnow: 'Your child should know the names of common polygons and understand that the number of sides determines the name.',
      commonMisconceptions: 'Children often confuse square and rectangle. Remind them that a square is a special type of rectangle with all sides equal.',
      dinnerTableQuestion: '"Can you find a hexagon somewhere on the dinner table or in the room we are sitting in?"',
      nextLessonPreview: 'Next time: How do we calculate the perimeter of a shape — and what is it used for in real life?',
    },
    realWorldBridge: {
      context: 'Arkitektur, natur, sport',
      content: 'Former er overalt rundt deg. Fotballbanen er et rektangel. Stoppskilt er oktagon. Honningkaker er sekskanter — og det er ikke tilfeldig. Bier valgte sekskanten fordi den bruker minst voks for å lagre mest honning. Naturen løser geometriske optimaliseringsproblemer. Du gjør det samme.',
    },
    coreConcept: {
      definition: 'En polygon er en lukket, flat figur med rette sider. Antall sider bestemmer navnet.',
      terminology: ['polygon', 'trekant', 'kvadrat', 'rektangel', 'pentagon', 'heksagon', 'hjørner', 'sider'],
      explanation: 'Vi klassifiserer polygoner etter antall sider: 3 sider = trekant, 4 sider = firkant, 5 sider = pentagon, 6 sider = heksagon. En regulær polygon har alle sider og vinkler like. En trekant med alle sider like heter likesidet trekant.',
      workedExamples: [
        {
          problem: 'Hva heter en polygon med 8 like sider?',
          solution: 'Oktagon (regulær oktagon)',
          explanation: 'Okta- betyr åtte på gresk. Stoppskilt er et kjent eksempel på et (nesten regulært) oktagon.',
        },
      ],
    },
    interactiveComponent: {
      type: 'drawing',
      description: 'Tegn polygoner ved å klikke for å plassere hjørnepunkter. Telle sider og hjørner automatisk.',
      phetUrl: null,
    },
    practiceQuestions: [
      {
        question: 'Et stoppskilt har 8 sider. Hva heter denne formen?',
        type: 'multiple_choice',
        options: ['Heksagon', 'Oktagon', 'Pentagon', 'Dekagon'],
        correctAnswer: 'Oktagon',
        explanation: 'Okta = åtte. En polygon med 8 sider er et oktagon.',
        solutionSteps: [
          '🛑 Tell sidene på et stoppskilt: det har 8 sider',
          'Prefiks for 8 på gresk = okta (tenk: octopus = 8 armer!)',
          'En polygon med 8 sider = okta + gon',
          'Svaret er Oktagon ✓',
        ],
      },
      {
        question: 'Hvilken polygon har 6 sider?',
        type: 'multiple_choice',
        options: ['Pentagon', 'Heptagon', 'Heksagon', 'Oktagon'],
        correctAnswer: 'Heksagon',
        explanation: 'Hexa = seks på gresk. En heksagon har 6 sider — som en honningkake.',
        solutionSteps: [
          '🐝 Tenk på en bikakecelle — den har 6 sider',
          'Prefiks for 6 på gresk = hexa',
          'Hexa + gon = Heksagon',
          'En heksagon har 6 sider ✓',
        ],
      },
      {
        question: 'En regulær polygon har alle sider like lange. Er et rektangel (som ikke er kvadrat) en regulær polygon?',
        type: 'multiple_choice',
        options: ['Ja', 'Nei', 'Noen ganger', 'Umulig å si'],
        correctAnswer: 'Nei',
        explanation: 'Et rektangel har 4 rette vinkler, men sidene er ikke alle like lange. Bare et kvadrat er en regulær firkant.',
        solutionSteps: [
          '📐 Regulær polygon = alle sider LIKE LANGE og alle vinkler like',
          'Et rektangel har 2 lange sider og 2 korte sider',
          'Er alle sidene like lange? NEI — de er forskjellige',
          'Konklusjon: Nei, et rektangel er ikke regulær ✓',
        ],
      },
      {
        question: 'Hvor mange sider har en trekant?',
        type: 'multiple_choice',
        options: ['2', '3', '4', '5'],
        correctAnswer: '3',
        explanation: 'En trekant er en polygon med nøyaktig 3 sider og 3 hjørner. "Tre" kommer fra "tre" sider.',
        solutionSteps: [
          '📐 "Trekant" inneholder ordet "tre"',
          'Tre → 3 sider',
          'En trekant har 3 sider og 3 hjørner',
          'Svaret er 3 ✓',
        ],
      },
      {
        question: 'Hva kaller vi hjørnepunktene i en polygon?',
        type: 'multiple_choice',
        options: ['Sider', 'Kanter', 'Hjørner (vertices)', 'Diagonaler'],
        correctAnswer: 'Hjørner (vertices)',
        explanation: 'Hjørnene (på engelsk: vertices, entall: vertex) er punktene der sidene møtes i en polygon.',
        solutionSteps: [
          '📍 I en polygon møtes sidene i punkter',
          'Disse punktene kalles hjørner',
          'På engelsk heter de "vertices" (entall: vertex)',
          'Svaret er Hjørner (vertices) ✓',
        ],
      },
      {
        question: 'Hvor mange sider har en pentagon?',
        type: 'multiple_choice',
        options: ['4', '5', '6', '7'],
        correctAnswer: '5',
        explanation: 'Penta- betyr fem på gresk. En pentagon har 5 sider.',
        solutionSteps: [
          '🏛 Penta = fem på gresk',
          'Penta + gon = Pentagon',
          'En pentagon har 5 sider ✓',
        ],
      },
      {
        question: 'Hvor mange rette vinkler (90°) har et kvadrat?',
        type: 'multiple_choice',
        options: ['1', '2', '3', '4'],
        correctAnswer: '4',
        explanation: 'Et kvadrat har 4 hjørner, og hvert hjørne er en rett vinkel på 90°. Totalt: 4 rette vinkler.',
        solutionSteps: [
          '⬛ Et kvadrat er en firkantet form',
          'Hvert hjørne i et kvadrat er 90°',
          'Antall hjørner: 4',
          'Antall rette vinkler: 4 ✓',
        ],
      },
      {
        question: 'Hva heter en polygon med 10 sider?',
        type: 'multiple_choice',
        options: ['Nonagon', 'Dekagon', 'Heksagon', 'Oktagon'],
        correctAnswer: 'Dekagon',
        explanation: 'Deka- betyr ti på gresk. En polygon med 10 sider er et dekagon.',
        solutionSteps: [
          '🔟 Deka = ti på gresk (tenk: dekade = 10 år)',
          'Deka + gon = Dekagon',
          'En polygon med 10 sider heter dekagon ✓',
        ],
      },
      {
        question: 'Bier bruker denne formen i bikaker fordi den er mest effektiv. Hvilken form er det?',
        type: 'multiple_choice',
        options: ['Trekant', 'Kvadrat', 'Heksagon', 'Sirkel'],
        correctAnswer: 'Heksagon',
        explanation: 'Bier bruker heksagoner (sekskanter) i bikakene sine fordi denne formen bruker minst voks for å lagre mest honning.',
        solutionSteps: [
          '🍯 Tenk på et bikakemønster',
          'Hver celle er formet som en sekskant',
          'Sekskant = 6 sider = Heksagon',
          'Svaret er Heksagon ✓',
        ],
      },
      {
        question: 'En regulær polygon har alle sider...',
        type: 'multiple_choice',
        options: ['Like lange', 'Forskjellig lengde', 'Buet', 'Parallelle'],
        correctAnswer: 'Like lange',
        explanation: 'En regulær polygon har alle sider like lange OG alle vinkler like store. Eksempel: et kvadrat er en regulær firkant.',
        solutionSteps: [
          '📏 "Regulær" polygon betyr at den er helt symmetrisk',
          'Alle sider = like lange',
          'Alle vinkler = like store',
          'Svaret er: Like lange ✓',
        ],
      },
    ],
    handsOnActivity: {
      title: 'Polygonjakten — Finn former i hjemmet ditt',
      materials: ['Papir og blyant', 'Linjal'],
      instructions: [
        'Gå rundt i huset ditt. Finn 2 eksempler på hver av disse formene: trekant, firkant, heksagon.',
        'Tegn dem eller skriv ned hva de var.',
        'Utfordring: Finn en form med mer enn 8 sider et sted i hjemmet.',
      ],
      timeMinutes: 10,
      whatToLookFor: 'Barnet bør begynne å "se" geometri i hverdagslige gjenstander — fliser, vinduer, mønstre på stoff.',
    },
    printableWorksheet: {
      title: 'Polygoner: Former rundt oss',
      sections: [
        {
          type: 'warmup',
          title: 'Sett navn på formene',
          instructions: 'Skriv det riktige navnet under hver form:',
          content: '[Trekant] [Kvadrat] [Heksagon] [Pentagon] [Oktagon] [Rektangel]',
        },
        {
          type: 'practice',
          title: '5 øvingsspørsmål',
          instructions: 'Sett ring rundt riktig svar:',
          content: '1. Et stoppskilt har 8 sider. Formen heter:\n   a) Heksagon   b) Dekagon   c) Pentagon   d) Oktagon\n\n2. Hvilken polygon har 6 sider?\n   a) Pentagon   b) Oktagon   c) Heksagon   d) Heptagon\n\n3. Hvor mange sider har en trekant?\n   a) 2   b) 4   c) 3   d) 5\n\n4. Hvor mange sider har en pentagon?\n   a) 4   b) 6   c) 7   d) 5\n\n5. En regulær polygon har alle sider:\n   a) Buet   b) Like lange   c) Forskjellig lengde   d) Parallelle',
        },
        {
          type: 'challenge',
          title: 'Mønsterdesign',
          instructions: 'Tegn et mønster som bare bruker heksagoner (som et bikakemønster). Hvorfor tror du bier valgte denne formen?',
          content: '[Tegnerom]',
        },
      ],
    },
    parentGuide: {
      whatChildShouldKnow: 'Barnet ditt bør kjenne navnene på vanlige polygoner og forstå at antall sider bestemmer navnet.',
      commonMisconceptions: 'Barn forveksler ofte kvadrat og rektangel. Minn dem på at et kvadrat er en spesiell type rektangel med alle sider like lange.',
      dinnerTableQuestion: '"Kan du finne en heksagon et sted på middagsbordet eller i rommet vi sitter i?"',
      nextLessonPreview: 'Neste gang: Hvordan beregner vi omkretsen av en figur — og hva brukes det til i det virkelige livet?',
    },
    openDoor: 'Du kan nå identifisere polygoner. Men hva skjer inne i en polygon? Hva er summen av alle vinklene i en trekant? I en firkant? Finnes det et mønster? Det er faktisk en av de vakreste oppdagelsene i geometrien.',
    curriculumSources: ['NCERT Grade 5 Ch. 2 — Shapes and Angles', 'NDLA Matematikk 5. trinn — Geometri'],
    learningObjectives: [
      'Identifisere og navngi vanlige polygoner',
      'Forstå sammenhengen mellom antall sider og polygonnavn',
      'Skille mellom regulære og irregulære polygoner',
    ],
    generatedAt: new Date(),
    version: 1,
  },

  'algebra-what-is-variable': {
    id: 'algebra-what-is-variable',
    pathwayId: 'math-algebra-intro-8-12',
    subject: 'mathematics',
    strand: 'Algebra',
    ageBand: '8-12',
    phase: 'builder',
    level: 2,
    language: 'no',
    title: 'Hva skjer når vi gir ukjente tall et navn?',
    titleEn: 'What happens when we give unknown numbers a name?',
    durationMinutes: 30,
    saganHook: {
      type: 'question',
      content: 'Tenk deg at du kjøper et antall boller. Du vet ikke nøyaktig hvor mange — kanskje er det 3, kanskje 7, kanskje 20. Men du vet at hver bolle koster 12 kroner. Hvordan skriver du opp den totale prisen uten å vite antallet? Matematikere løste dette for over tusen år siden ved å gjøre noe genialt: de ga det ukjente et navn.',
    },
    saganHookEn: "Imagine you're buying buns. You don't know exactly how many — maybe 3, maybe 7, maybe 20. But you know each bun costs 12 kroner. How do you write down the total price without knowing the number? Mathematicians solved this over a thousand years ago by doing something brilliant: they gave the unknown a name.",
    realWorldBridgeEn: {
      context: 'Shopping, game scores, temperature',
      content: "Video games use variables all the time. 'score = score + 10' means: take the current score, add 10, and let that be the new score. Programmers write this hundreds of times per game. They don't need to know what the score is right now — they just describe the rule. That is algebra.",
    },
    coreConceptEn: {
      definition: 'A variable is a symbol (usually a letter) that represents an unknown number or a value that can change.',
      terminology: ['variable', 'constant', 'expression', 'coefficient'],
      explanation: "When we write 3x, it means '3 times x'. We don't know what x is, but we can work with the expression. If x = 5, then 3x = 15. The coefficient is the number in front of the variable (here: 3). A constant is a number that doesn't change (like 7 in the expression 3x + 7).",
      workedExamples: [
        {
          problem: 'What is the value of 4n + 3 when n = 6?',
          solution: '4(6) + 3 = 24 + 3 = 27',
          explanation: 'Replace n with 6 everywhere. 4 times 6 is 24. Add 3. The answer is 27.',
        },
        {
          problem: 'Write an expression for: "Three times a number, minus 5"',
          solution: '3x - 5',
          explanation: '"A number" is our variable — call it x. Three times it: 3x. Minus 5: 3x - 5.',
        },
      ],
    },
    practiceQuestionsEn: [
      {
        question: 'What is the value of 5y + 2 when y = 3?',
        type: 'multiple_choice',
        options: ['17', '15', '10', '25'],
        correctAnswer: '17',
        explanation: '5 × 3 + 2 = 15 + 2 = 17. Always replace the variable with the given value.',
        solutionSteps: [
          '🔢 Write the expression: 5y + 2, and substitute y = 3',
          'Replace y with 3: 5 × 3 + 2',
          'Do the multiplication first: 5 × 3 = 15',
          'Add 2: 15 + 2 = 17 ✓',
        ],
      },
      {
        question: 'In the expression 7m + 4, what is the coefficient?',
        type: 'multiple_choice',
        options: ['4', 'm', '7', '11'],
        correctAnswer: '7',
        explanation: 'The coefficient is the number multiplied by the variable. In 7m, the coefficient is 7.',
        solutionSteps: [
          '🔤 The expression is: 7m + 4',
          'The variable is the letter: m',
          'The coefficient = the number multiplied by the variable (in front of m)',
          'In front of m is: 7 → the coefficient is 7 ✓',
        ],
      },
      {
        question: 'Write an expression for "twice a number, plus 8":',
        type: 'multiple_choice',
        options: ['2n + 8', 'n + 8', '8n + 2', '2 + 8n'],
        correctAnswer: '2n + 8',
        explanation: '"Twice a number" = 2n. "Plus 8" = + 8. Together: 2n + 8.',
        solutionSteps: [
          '🔡 "A number" = we choose a variable, e.g. n',
          '"Twice a number" = 2 times n = 2n',
          '"Plus 8" = + 8',
          'Put it together: 2n + 8 ✓',
        ],
      },
      {
        question: 'What is the value of 4x when x = 5?',
        type: 'multiple_choice',
        options: ['9', '20', '45', '15'],
        correctAnswer: '20',
        explanation: '4x means 4 times x. Substitute x = 5: 4 × 5 = 20.',
        solutionSteps: [
          '🔢 The expression is 4x, which means 4 × x',
          'Substitute x = 5',
          '4 × 5 = 20',
          'The answer is 20 ✓',
        ],
      },
      {
        question: 'In the expression 3n + 7, what is the constant?',
        type: 'multiple_choice',
        options: ['3', 'n', '7', '3n'],
        correctAnswer: '7',
        explanation: 'A constant is a number that does not change — it has no variable attached. In 3n + 7, the constant is 7.',
        solutionSteps: [
          '📌 Look at the expression: 3n + 7',
          '3n is the term with the variable n',
          '7 is a number that does NOT change, whatever n is',
          'The number that never changes = constant = 7 ✓',
        ],
      },
      {
        question: 'Which algebraic expression means "a number minus 9"?',
        type: 'multiple_choice',
        options: ['x - 9', '9 - x', 'x + 9', '9x'],
        correctAnswer: 'x - 9',
        explanation: '"A number" is our variable — call it x. "Minus 9" means we subtract 9. The expression is x - 9.',
        solutionSteps: [
          '🔡 "A number" = choose a variable, e.g. x',
          '"Minus 9" = subtract 9',
          'Put it together: x - 9 ✓',
        ],
      },
      {
        question: 'What is the value of 2a + 5 when a = 4?',
        type: 'multiple_choice',
        options: ['11', '13', '8', '18'],
        correctAnswer: '13',
        explanation: '2a + 5 with a = 4: 2 × 4 + 5 = 8 + 5 = 13.',
        solutionSteps: [
          '🔢 Expression: 2a + 5, and a = 4',
          'Substitute: 2 × 4 + 5',
          'Calculate multiplication: 2 × 4 = 8',
          'Add 5: 8 + 5 = 13 ✓',
        ],
      },
      {
        question: 'Which of these is a variable?',
        type: 'multiple_choice',
        options: ['7', 'x', '+', '='],
        correctAnswer: 'x',
        explanation: 'A variable is a letter that represents an unknown number. x is a variable; 7 is a number (constant).',
        solutionSteps: [
          '🔤 A variable is a symbol for an unknown number',
          'It is usually a letter like x, y, n or m',
          '7 is a fixed number — not a variable',
          'x is the variable here ✓',
        ],
      },
      {
        question: 'What is the value of 6m - 4 when m = 3?',
        type: 'multiple_choice',
        options: ['14', '22', '18', '10'],
        correctAnswer: '14',
        explanation: '6m - 4 with m = 3: 6 × 3 - 4 = 18 - 4 = 14.',
        solutionSteps: [
          '🔢 Expression: 6m - 4, and m = 3',
          'Substitute: 6 × 3 - 4',
          'Calculate multiplication: 6 × 3 = 18',
          'Subtract 4: 18 - 4 = 14 ✓',
        ],
      },
      {
        question: 'Which expression means "three times a number, plus 2"?',
        type: 'multiple_choice',
        options: ['3n + 2', '3 + 2n', 'n + 3 + 2', '2n + 3'],
        correctAnswer: '3n + 2',
        explanation: '"Three times a number" = 3n. "Plus 2" = + 2. Together: 3n + 2.',
        solutionSteps: [
          '🔡 "A number" = variable n',
          '"Three times a number" = 3 × n = 3n',
          '"Plus 2" = + 2',
          'Put it together: 3n + 2 ✓',
        ],
      },
    ],
    handsOnActivityEn: {
      title: 'Mystery bag — Variables with beans',
      materials: ['A small bag or box', '20 beans or buttons', 'Paper and pencil'],
      instructions: [
        'Place an unknown number of beans in the bag without looking. Let the unknown number be called "n".',
        'Place 4 beans next to the bag. Total value: n + 4.',
        'Open the bag and count. What was n? What was n + 4?',
        'Repeat: take double the number of beans in the bag + 3 extra. Write the expression: 2n + 3.',
      ],
      timeMinutes: 10,
      whatToLookFor: "The child should start to see that the variable 'is' the unknown beans — it represents a number we don't know yet, but can work with.",
    },
    openDoorEn: "You can now read and evaluate algebraic expressions. But an expression doesn't have one answer. An equation does. What is the difference between 2x + 3 and 2x + 3 = 11? And how do you find x in the second case? That is the next step — and it is where algebra starts to feel like cracking codes.",
    learningObjectivesEn: [
      'Understand what a variable represents',
      'Evaluate algebraic expressions for given values',
      'Translate word problems into algebraic expressions',
    ],
    parentGuideEn: {
      whatChildShouldKnow: 'Your child should understand what a variable is and be able to substitute values into simple algebraic expressions.',
      commonMisconceptions: "Children often assume the variable is always x and always means the same number. Help them understand that any letter can be used, and that the value can vary.",
      dinnerTableQuestion: `"Imagine you don't know what something costs. Call it x. If you buy 3 of them plus a drink for £2 — what is the total price as an expression?"`,
      nextLessonPreview: 'Next time: The difference between an expression and an equation — and what it means to "solve" something in algebra.',
    },
    realWorldBridge: {
      context: 'Shopping, spillpoeng, temperatur',
      content: 'Dataspill bruker variabler hele tiden. "score = score + 10" betyr: ta nåværende poengsum, legg til 10, og la det være den nye poengsummen. Programmerere skriver dette hundrevis av ganger per spill. De trenger ikke vite hva poengsummen er akkurat nå — de bare beskriver regelen. Det er algebra.',
    },
    coreConcept: {
      definition: 'En variabel er et symbol (vanligvis en bokstav) som representerer et ukjent tall eller en verdi som kan variere.',
      terminology: ['variabel', 'konstant', 'uttrykk', 'koeffisient'],
      explanation: 'Når vi skriver 3x, betyr det "3 ganger x". Vi vet ikke hva x er, men vi kan jobbe med uttrykket. Hvis x = 5, er 3x = 15. Koeffisienten er tallet foran variabelen (her: 3). En konstant er et tall som ikke endres (som 7 i uttrykket 3x + 7).',
      workedExamples: [
        {
          problem: 'Hva er verdien av 4n + 3 når n = 6?',
          solution: '4(6) + 3 = 24 + 3 = 27',
          explanation: 'Erstatt n med 6 overalt. 4 ganger 6 er 24. Legg til 3. Svaret er 27.',
        },
        {
          problem: 'Skriv et uttrykk for: "Tre ganger et tall, minus 5"',
          solution: '3x - 5',
          explanation: '"Et tall" er vår variabel — kall det x. Tre ganger det: 3x. Minus 5: 3x - 5.',
        },
      ],
    },
    interactiveComponent: {
      type: 'number_input',
      description: 'Skriv inn en verdi for x. Se hvordan uttrykket 2x + 3 endres automatisk.',
      phetUrl: null,
    },
    practiceQuestions: [
      {
        question: 'Hva er verdien av 5y + 2 når y = 3?',
        type: 'multiple_choice',
        options: ['17', '15', '10', '25'],
        correctAnswer: '17',
        explanation: '5 × 3 + 2 = 15 + 2 = 17. Erstatt alltid variabelen med den gitte verdien.',
        solutionSteps: [
          '🔢 Skriv uttrykket: 5y + 2, og sett inn y = 3',
          'Erstatt y med 3: 5 × 3 + 2',
          'Regn multiplikasjonen først: 5 × 3 = 15',
          'Legg til 2: 15 + 2 = 17 ✓',
        ],
      },
      {
        question: 'I uttrykket 7m + 4, hva er koeffisienten?',
        type: 'multiple_choice',
        options: ['4', 'm', '7', '11'],
        correctAnswer: '7',
        explanation: 'Koeffisienten er tallet som ganges med variabelen. I 7m er koeffisienten 7.',
        solutionSteps: [
          '🔤 Uttrykket er: 7m + 4',
          'Variabelen er bokstaven: m',
          'Koeffisienten = tallet som ganges med variabelen (foran m)',
          'Foran m står: 7 → koeffisienten er 7 ✓',
        ],
      },
      {
        question: 'Skriv et uttrykk for "det dobbelte av et tall, pluss 8":',
        type: 'multiple_choice',
        options: ['2n + 8', 'n + 8', '8n + 2', '2 + 8n'],
        correctAnswer: '2n + 8',
        explanation: '"Det dobbelte av et tall" = 2n. "Pluss 8" = + 8. Samlet: 2n + 8.',
        solutionSteps: [
          '🔡 "Et tall" = vi velger en variabel, f.eks. n',
          '"Det dobbelte av et tall" = 2 ganger n = 2n',
          '"Pluss 8" = + 8',
          'Sett det sammen: 2n + 8 ✓',
        ],
      },
      {
        question: 'Hva er verdien av 4x når x = 5?',
        type: 'multiple_choice',
        options: ['9', '20', '45', '15'],
        correctAnswer: '20',
        explanation: '4x betyr 4 ganger x. Sett inn x = 5: 4 × 5 = 20.',
        solutionSteps: [
          '🔢 Uttrykket er 4x, som betyr 4 × x',
          'Sett inn x = 5',
          '4 × 5 = 20',
          'Svaret er 20 ✓',
        ],
      },
      {
        question: 'I uttrykket 3n + 7, hva er konstanten?',
        type: 'multiple_choice',
        options: ['3', 'n', '7', '3n'],
        correctAnswer: '7',
        explanation: 'En konstant er et tall som ikke endres — det har ingen variabel. I 3n + 7 er 7 konstanten.',
        solutionSteps: [
          '📌 Se på uttrykket: 3n + 7',
          '3n er leddet med variabelen n',
          '7 er et tall som IKKE endres, uansett hva n er',
          'Tallet som ikke endres = konstanten = 7 ✓',
        ],
      },
      {
        question: 'Hvilket algebraisk uttrykk betyr "et tall minus 9"?',
        type: 'multiple_choice',
        options: ['x - 9', '9 - x', 'x + 9', '9x'],
        correctAnswer: 'x - 9',
        explanation: '"Et tall" er vår variabel — kall det x. "Minus 9" betyr vi trekker fra 9. Uttrykket er x - 9.',
        solutionSteps: [
          '🔡 "Et tall" = velg en variabel, f.eks. x',
          '"Minus 9" = trekk fra 9',
          'Sett det sammen: x - 9 ✓',
        ],
      },
      {
        question: 'Hva er verdien av 2a + 5 når a = 4?',
        type: 'multiple_choice',
        options: ['11', '13', '8', '18'],
        correctAnswer: '13',
        explanation: '2a + 5 med a = 4: 2 × 4 + 5 = 8 + 5 = 13.',
        solutionSteps: [
          '🔢 Uttrykket: 2a + 5, og a = 4',
          'Sett inn: 2 × 4 + 5',
          'Regn multiplikasjon: 2 × 4 = 8',
          'Legg til 5: 8 + 5 = 13 ✓',
        ],
      },
      {
        question: 'Hvilken av disse er en variabel?',
        type: 'multiple_choice',
        options: ['7', 'x', '+', '='],
        correctAnswer: 'x',
        explanation: 'En variabel er en bokstav som representerer et ukjent tall. x er en variabel; 7 er et tall (konstant).',
        solutionSteps: [
          '🔤 En variabel er et symbol for et ukjent tall',
          'Vanligvis er det en bokstav som x, y, n eller m',
          '7 er et bestemt tall — ikke en variabel',
          'x er variabelen her ✓',
        ],
      },
      {
        question: 'Hva er verdien av 6m - 4 når m = 3?',
        type: 'multiple_choice',
        options: ['14', '22', '18', '10'],
        correctAnswer: '14',
        explanation: '6m - 4 med m = 3: 6 × 3 - 4 = 18 - 4 = 14.',
        solutionSteps: [
          '🔢 Uttrykket: 6m - 4, og m = 3',
          'Sett inn: 6 × 3 - 4',
          'Regn multiplikasjon: 6 × 3 = 18',
          'Trekk fra 4: 18 - 4 = 14 ✓',
        ],
      },
      {
        question: 'Hvilket uttrykk betyr "tre ganger et tall pluss 2"?',
        type: 'multiple_choice',
        options: ['3n + 2', '3 + 2n', 'n + 3 + 2', '2n + 3'],
        correctAnswer: '3n + 2',
        explanation: '"Tre ganger et tall" = 3n. "Pluss 2" = + 2. Samlet: 3n + 2.',
        solutionSteps: [
          '🔡 "Et tall" = variabel n',
          '"Tre ganger et tall" = 3 × n = 3n',
          '"Pluss 2" = + 2',
          'Sett det sammen: 3n + 2 ✓',
        ],
      },
    ],
    handsOnActivity: {
      title: 'Mysteriepose — Variabler med bønner',
      materials: ['En liten pose eller boks', '20 bønner eller knapper', 'Papir og blyant'],
      instructions: [
        'Legg et ukjent antall bønner i posen uten å se. La det ukjente antallet hete "n".',
        'Legg 4 bønner ved siden av posen. Total verdi: n + 4.',
        'Åpne posen og tell. Hva var n? Hva var n + 4?',
        'Gjenta: ta dobbelt så mange bønner som i posen + 3 ekstra. Skriv uttrykket: 2n + 3.',
      ],
      timeMinutes: 10,
      whatToLookFor: 'Barnet bør begynne å se at variabelen "er" de ukjente bønnene — den representerer et tall vi ikke vet ennå, men kan jobbe med.',
    },
    printableWorksheet: {
      title: 'Variabler og uttrykk',
      sections: [
        {
          type: 'warmup',
          title: 'Finn verdien',
          instructions: 'Beregn verdiene:',
          content: '1. 3n + 1 når n = 4: ___\n2. 2x - 3 når x = 7: ___\n3. 5y når y = 2: ___\n4. m + 9 når m = 11: ___',
        },
        {
          type: 'practice',
          title: '5 øvingsspørsmål',
          instructions: 'Sett ring rundt riktig svar:',
          content: '1. Hva er 5y + 2 når y = 3?\n   a) 15   b) 25   c) 10   d) 17\n\n2. Koeffisienten i 7m + 4 er:\n   a) 4   b) m   c) 11   d) 7\n\n3. Hva er 4x når x = 5?\n   a) 9   b) 15   c) 20   d) 45\n\n4. Konstanten i 3n + 7 er:\n   a) 3   b) n   c) 3n   d) 7\n\n5. "Det dobbelte av et tall pluss 8" som uttrykk er:\n   a) n + 8   b) 8n + 2   c) 2n + 8   d) 2 + 8n',
        },
        {
          type: 'challenge',
          title: 'Kodeknekking',
          instructions: 'En kode bruker formelen: Kode = 3n + 7. Hva er koden når n = 5? Og: hvis koden er 22, hva er n?',
          content: 'n = 5 → Kode = ___\nKode = 22 → n = ___',
        },
      ],
    },
    parentGuide: {
      whatChildShouldKnow: 'Barnet bør forstå hva en variabel er og kan sette inn verdier i enkle algebraiske uttrykk.',
      commonMisconceptions: 'Barn antar ofte at variabelen alltid er x og alltid betyr det samme tall. Hjelp dem å forstå at enhver bokstav kan brukes, og at verdien kan variere.',
      dinnerTableQuestion: '"Tenk deg at du ikke vet hva noe koster. Kall det x. Hvis du kjøper 3 av det pluss en brus til 25 kr — hva er den totale prisen som et uttrykk?"',
      nextLessonPreview: 'Neste gang: Forskjellen mellom et uttrykk og en likning — og hva det vil si å "løse" noe i algebra.',
    },
    openDoor: 'Du kan nå lese og evaluere algebraiske uttrykk. Men et uttrykk har ikke ett svar. En likning har det. Hva er forskjellen mellom 2x + 3 og 2x + 3 = 11? Og hvordan finner du x i det andre tilfellet? Det er neste steg — og det er der algebra begynner å føles som å knekke koder.',
    curriculumSources: ['NCERT Grade 6 Ch. 11 — Algebra', 'NDLA Matematikk 7. trinn — Algebra'],
    learningObjectives: [
      'Forstå hva en variabel representerer',
      'Evaluere algebraiske uttrykk for gitte verdier',
      'Oversette ordproblemer til algebraiske uttrykk',
    ],
    generatedAt: new Date(),
    version: 1,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ✨ SPARK — Lesson 1: Shapes (Ages 4–6)
  // Synthesised from: NCERT Math-Magic Class 1-2 · Norwegian LK20 1-2.trinn
  //                   Finnish POPS Grades 1-2 · Common Core K.G
  // ═══════════════════════════════════════════════════════════════════════════
  'shapes-why-hexagons': {
    id: 'shapes-why-hexagons',
    pathwayId: 'math-shapes-4-6',
    subject: 'mathematics',
    strand: 'Geometri',
    ageBand: '4-7',
    phase: 'spark',
    level: 1,
    language: 'no',
    title: 'Hvorfor bygger bier hjemmene sine med sekskantet form?',
    titleEn: 'Why do bees build their homes in a six-sided shape?',
    durationMinutes: 15,
    saganHook: {
      type: 'question',
      content: 'Tenk deg at du er en liten bie. Du har ingen linjal, ingen kalkulator, ingen tegneprogram. Og likevel — hvert eneste hus i bikolonien din er en perfekt sekskant. Alle passer nøyaktig sammen, uten hull og uten overlapp. Hvem lærte bien å gjøre det?',
    },
    saganHookEn: 'Imagine you are a tiny bee. You have no ruler, no calculator, no drawing program. And yet — every single home in your bee colony is a perfect hexagon. They all fit together exactly, with no gaps and no overlaps. Who taught the bee to do that?',
    realWorldBridge: {
      context: 'Honningkake, snøfnugg, appelsinskiver',
      content: 'Former er overalt rundt deg! Pizzabiter er trekanter. Hjul er sirkler. Vinduer er rektangler. Bienes hus er sekskantar. Når du lærer å gjenkjenne former, begynner du å se verden slik en matematiker ser den.',
    },
    realWorldBridgeEn: {
      context: 'Honeycomb, snowflakes, orange slices',
      content: 'Shapes are everywhere around you! Pizza slices are triangles. Wheels are circles. Windows are rectangles. Bee homes are hexagons. When you learn to recognise shapes, you start to see the world the way a mathematician does.',
    },
    coreConcept: {
      definition: 'En form er omrisset av et objekt — kanten som skiller innsiden fra utsiden. Ulike former har ulike antall sider og hjørner.',
      terminology: ['sirkel', 'trekant', 'firkant', 'rektangel', 'sekskant', 'side', 'hjørne'],
      explanation: 'Vi kjenner igjen former ved å telle sidene og hjørnene deres. En trekant har 3 sider og 3 hjørner. En firkant har 4 like sider. Et rektangel har 4 sider — to lange og to korte. En sekskant har 6 sider, akkurat som bienes hus. En sirkel har ingen sider og ingen hjørner — den er helt rund!',
      workedExamples: [
        {
          problem: 'Du ser et stoppskilt. Hvor mange sider har det?',
          solution: '8 sider — det er en åttekant!',
          explanation: 'Tell sidene på et stoppskilt: 1, 2, 3, 4, 5, 6, 7, 8. Formen kalles en oktagon. Den er lett å kjenne igjen selv når du bare ser kanten av den — det er nettopp meningen!',
        },
        {
          problem: 'En appelsin er delt i 8 båter. Hvilken form ligner hver bât mest på?',
          solution: 'En trekant (nesten!)',
          explanation: 'Hver appelsinbåt er smal i begge ender og bred i midten — den ligner på en trekant. Matematikk gjemmer seg til og med i frukten din!',
        },
      ],
    },
    coreConceptEn: {
      definition: 'A shape is the outline of an object — the edge that separates the inside from the outside. Different shapes have different numbers of sides and corners.',
      terminology: ['circle', 'triangle', 'square', 'rectangle', 'hexagon', 'side', 'corner'],
      explanation: 'We recognise shapes by counting their sides and corners. A triangle has 3 sides and 3 corners. A square has 4 equal sides. A rectangle has 4 sides — two long and two short. A hexagon has 6 sides, just like the bee\'s home. A circle has no sides and no corners — it is completely round!',
      workedExamples: [
        {
          problem: 'You see a stop sign. How many sides does it have?',
          solution: '8 sides — it\'s an octagon!',
          explanation: 'Count the sides of a stop sign: 1, 2, 3, 4, 5, 6, 7, 8. The shape is called an octagon. It is easy to recognise even if you only see the edge — that is exactly the point!',
        },
        {
          problem: 'An orange is divided into 8 segments. What shape does each segment look like?',
          solution: 'A triangle (almost!)',
          explanation: 'Each orange segment is narrow at both ends and wide in the middle — it looks like a triangle. Mathematics is hiding in your fruit!',
        },
      ],
    },
    interactiveComponent: {
      type: 'drag_drop',
      description: 'Dra former til riktig navn — sirkel, trekant, firkant, sekskant',
      phetUrl: null,
    },
    practiceQuestions: [
      {
        question: 'En honningkakecelle er en sekskant. Hvor mange sider har den?',
        type: 'multiple_choice',
        options: ['4', '5', '6', '8'],
        correctAnswer: '6',
        explanation: 'En sekskant har 6 sider — akkurat som "seks" i navnet! Bier bygger sekskanter fordi de passer perfekt sammen uten mellomrom.',
        solutionSteps: ['🐝 Tenk på et biehus', 'Tell sidene: 1, 2, 3, 4, 5, 6', 'Sekskant = 6 sider ✓'],
      },
      {
        question: 'Hvilken form har hjulet på en sykkel?',
        type: 'multiple_choice',
        options: ['Trekant', 'Sirkel', 'Firkant', 'Sekskant'],
        correctAnswer: 'Sirkel',
        explanation: 'Et sykkelhjul er en sirkel — ingen hjørner, helt rund. Sirkler ruller jevnt fordi alle punkter på kanten er like langt fra midten.',
        solutionSteps: ['🚲 Tenk på et hjul', 'Har det hjørner? Nei!', 'Er det rundt? Ja!', 'Sirkel ✓'],
      },
      {
        question: 'En pizzabit har trekantet form. Hvor mange hjørner har en trekant?',
        type: 'multiple_choice',
        options: ['2', '3', '4', '6'],
        correctAnswer: '3',
        explanation: '"Tri" i trekant betyr tre! En trekant har alltid 3 sider og 3 hjørner.',
        solutionSteps: ['🍕 Tenk på en pizzabit', 'Tell hjørnene: 1, 2, 3', 'Trekant = 3 hjørner ✓'],
      },
      {
        question: 'Du ser på et vindu som er høyere enn det er bredt. Hvilken form er det?',
        type: 'multiple_choice',
        options: ['Firkant', 'Rektangel', 'Sirkel', 'Trekant'],
        correctAnswer: 'Rektangel',
        explanation: 'Et rektangel har 4 sider — men to er lengre enn de andre. Vinduet er høyere enn det er bredt, så det er et rektangel.',
        solutionSteps: ['🪟 Se på vinduet', 'Tel sider: 1, 2, 3, 4', 'Er alle sider like lange? Nei', 'Rektangel = 4 sider, 2 lange og 2 korte ✓'],
      },
      {
        question: 'Snøfnugg har alltid 6 armer. Hvilken form ligner de mest på?',
        type: 'multiple_choice',
        options: ['Trekant', 'Sirkel', 'Sekskant', 'Firkant'],
        correctAnswer: 'Sekskant',
        explanation: 'Snøfnugg vokser alltid i et sekskant-mønster! Vannmolekyler kobler seg sammen i sekskantet form. Naturen elsker sekskanter.',
        solutionSteps: ['❄️ Tenk på et snøfnugg', '6 armer = 6 retninger', 'Sekskant = 6 sider', 'Sekskant ✓'],
      },
      {
        question: 'Hvor mange hjørner har en firkant?',
        type: 'multiple_choice',
        options: ['2', '3', '4', '5'],
        correctAnswer: '4',
        explanation: 'En firkant har 4 sider OG 4 hjørner. Antallet sider og hjørner er alltid det samme!',
        solutionSteps: ['⬛ Tenk på en firkant', 'Tell hjørnene: øvre-venstre, øvre-høyre, nedre-høyre, nedre-venstre', '= 4 hjørner ✓'],
      },
    ],
    practiceQuestionsEn: [
      {
        question: 'A honeycomb cell is a hexagon. How many sides does it have?',
        type: 'multiple_choice',
        options: ['4', '5', '6', '8'],
        correctAnswer: '6',
        explanation: 'A hexagon has 6 sides — just like "hex" means six! Bees build hexagons because they fit together perfectly with no gaps.',
        solutionSteps: ['🐝 Think of a beehive', 'Count the sides: 1, 2, 3, 4, 5, 6', 'Hexagon = 6 sides ✓'],
      },
      {
        question: 'What shape is a bicycle wheel?',
        type: 'multiple_choice',
        options: ['Triangle', 'Circle', 'Square', 'Hexagon'],
        correctAnswer: 'Circle',
        explanation: 'A bicycle wheel is a circle — no corners, completely round. Circles roll smoothly because every point on the edge is the same distance from the centre.',
        solutionSteps: ['🚲 Think of a wheel', 'Does it have corners? No!', 'Is it round? Yes!', 'Circle ✓'],
      },
      {
        question: 'A pizza slice has a triangular shape. How many corners does a triangle have?',
        type: 'multiple_choice',
        options: ['2', '3', '4', '6'],
        correctAnswer: '3',
        explanation: '"Tri" in triangle means three! A triangle always has 3 sides and 3 corners.',
        solutionSteps: ['🍕 Think of a pizza slice', 'Count the corners: 1, 2, 3', 'Triangle = 3 corners ✓'],
      },
      {
        question: 'You look at a window that is taller than it is wide. What shape is it?',
        type: 'multiple_choice',
        options: ['Square', 'Rectangle', 'Circle', 'Triangle'],
        correctAnswer: 'Rectangle',
        explanation: 'A rectangle has 4 sides — but two are longer than the others. The window is taller than it is wide, so it is a rectangle.',
        solutionSteps: ['🪟 Look at the window', 'Count sides: 1, 2, 3, 4', 'Are all sides equal? No', 'Rectangle = 4 sides, 2 long and 2 short ✓'],
      },
      {
        question: 'Snowflakes always have 6 arms. What shape do they look most like?',
        type: 'multiple_choice',
        options: ['Triangle', 'Circle', 'Hexagon', 'Square'],
        correctAnswer: 'Hexagon',
        explanation: 'Snowflakes always grow in a hexagonal pattern! Water molecules connect in a hexagonal shape. Nature loves hexagons.',
        solutionSteps: ['❄️ Think of a snowflake', '6 arms = 6 directions', 'Hexagon has 6 sides', 'Hexagon ✓'],
      },
      {
        question: 'How many corners does a square have?',
        type: 'multiple_choice',
        options: ['2', '3', '4', '5'],
        correctAnswer: '4',
        explanation: 'A square has 4 sides AND 4 corners. The number of sides and corners is always the same!',
        solutionSteps: ['⬛ Think of a square', 'Count corners: top-left, top-right, bottom-right, bottom-left', '= 4 corners ✓'],
      },
    ],
    handsOnActivity: {
      title: 'Formjakt i huset',
      materials: ['Papir og blyant', 'Fargeblyanter (valgfritt)'],
      instructions: [
        'Gå rundt i rommet ditt (eller hele huset!)',
        'Let etter gjenstander med disse formene: sirkel, trekant, firkant/rektangel, sekskant',
        'Skriv ned eller tegn det du finner — minst 2 ting per form',
        'Ekstra utfordring: Finn en form ute i naturen!',
      ],
      timeMinutes: 10,
      whatToLookFor: 'Barnet bør gjenkjenne at former finnes overalt i hverdagen. Spør: "Hvorfor tror du den formen ble valgt til akkurat den gjenstanden?"',
    },
    handsOnActivityEn: {
      title: 'Shape Hunt Around the House',
      materials: ['Paper and pencil', 'Coloured pencils (optional)'],
      instructions: [
        'Walk around your room (or the whole house!)',
        'Look for objects with these shapes: circle, triangle, square/rectangle, hexagon',
        'Write down or draw what you find — at least 2 things per shape',
        'Extra challenge: Find a shape in nature outside!',
      ],
      timeMinutes: 10,
      whatToLookFor: 'The child should recognise that shapes exist everywhere in daily life. Ask: "Why do you think that shape was chosen for that object?"',
    },
    printableWorksheet: {
      title: 'Former rundt oss',
      sections: [
        {
          type: 'warmup',
          title: 'Navngi formene',
          instructions: 'Skriv navnet på hver form:',
          content: '○ = ___\n△ = ___\n□ = ___\n⬡ = ___',
        },
        {
          type: 'practice',
          title: 'Tell sidene',
          instructions: 'Skriv antall sider:',
          content: 'Trekant: ___ sider\nFirkant: ___ sider\nSekskant: ___ sider\nSirkel: ___ sider',
        },
        {
          type: 'creative',
          title: 'Tegn din egen by med former',
          instructions: 'Bruk bare formene du har lært til å tegne et hus, en vei og et tre.',
          content: 'Hint: Hus = rektangel + trekant · Vei = rektangler · Tre = sirkel + rektangel',
        },
      ],
    },
    parentGuide: {
      whatChildShouldKnow: 'Barnet bør gjenkjenne og navngi sirkel, trekant, firkant, rektangel og sekskant, og telle sider og hjørner.',
      commonMisconceptions: 'Mange barn tror firkant og rektangel er helt forskjellige. Faktisk er en firkant et spesielt rektangel der alle sider er like lange — begge er riktige!',
      dinnerTableQuestion: '"Se rundt middagsbordet — hvilke former kan du se? Hva er tallerkenen? Bordet? Et glass?"',
      nextLessonPreview: 'Neste gang utforsker vi sirkler og kuler — og lurer på hvorfor planeter, bobler og appelsin alle er runde!',
    },
    parentGuideEn: {
      whatChildShouldKnow: 'The child should recognise and name circles, triangles, squares, rectangles, and hexagons, and count sides and corners.',
      commonMisconceptions: 'Many children think a square and rectangle are completely different. In fact a square is a special rectangle where all sides are equal — both are correct!',
      dinnerTableQuestion: '"Look around the dinner table — what shapes can you see? What shape is the plate? The table? A glass?"',
      nextLessonPreview: 'Next time we explore circles and spheres — and wonder why planets, bubbles, and oranges are all round!',
    },
    openDoor: 'Du kjenner nå formene som bygger verden. Men her er noe merkelig: bien velger aldri sirkelen eller trekanten — den velger alltid sekskanten. Og det er faktisk det mest effektive valget i hele naturen. Neste gang ser vi på sirkelen — den mest perfekte formen som finnes.',
    openDoorEn: 'You now know the shapes that build the world. But here is something strange: the bee never chooses the circle or triangle — it always chooses the hexagon. And that is actually the most efficient choice in all of nature. Next time we look at the circle — the most perfect shape that exists.',
    curriculumSources: [
      'NCERT Math-Magic Class 1: Chapter 1 — Shapes and Space',
      'NCERT Math-Magic Class 2: Chapter 3 — Shapes and Patterns',
      'Norwegian LK20 1-2. trinn: Geometri — gjenkjenne og beskrive 2D-former',
      'Finnish POPS Grades 1-2: Geometric shapes in the environment',
      'Common Core K.G.A.1, K.G.A.2, K.G.B.4 — Identify and describe shapes',
    ],
    learningObjectives: [
      'Gjenkjenne og navngi grunnleggende 2D-former: sirkel, trekant, firkant, rektangel, sekskant',
      'Telle sider og hjørner',
      'Finne former i hverdagslige omgivelser',
    ],
    learningObjectivesEn: [
      'Recognise and name basic 2D shapes: circle, triangle, square, rectangle, hexagon',
      'Count sides and corners',
      'Find and describe shapes in everyday surroundings',
    ],
    generatedAt: new Date(),
    version: 1,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔭 EXPLORER — Lesson 1: Multiplication (Ages 7–9)
  // Synthesised from: NCERT Math-Magic Class 3-4 · Norwegian LK20 3-4.trinn
  //                   Finnish POPS Grades 3-4 · Common Core 3.OA
  // ═══════════════════════════════════════════════════════════════════════════
  'multiplication-paper-moon': {
    id: 'multiplication-paper-moon',
    pathwayId: 'math-multiplication-7-9',
    subject: 'mathematics',
    strand: 'Tall og operasjoner',
    ageBand: '8-12',
    phase: 'explorer',
    level: 1,
    language: 'no',
    title: 'Hvis du brettet et papir 42 ganger, ville det nå Månen',
    titleEn: 'If you folded a piece of paper 42 times, it would reach the Moon',
    durationMinutes: 25,
    saganHook: {
      type: 'question',
      content: 'Ta et stykke papir. Brett det i to. Brett det i to igjen. Og igjen. Hver gang du bretter, dobler du tykkelsen. Etter 7 brettinger er papirstakken høyere enn dette rommet. Etter 23 brettinger er den 1 kilometer høy. Etter 42 brettinger ville den nå Månen — 384 000 kilometer unna. Er det mulig? Og hva har dette med ganging å gjøre?',
    },
    saganHookEn: 'Take a piece of paper. Fold it in half. Fold it in half again. And again. Every time you fold, you double the thickness. After 7 folds the stack is taller than this room. After 23 folds it is 1 kilometre high. After 42 folds it would reach the Moon — 384,000 kilometres away. Is that possible? And what does this have to do with multiplication?',
    realWorldBridge: {
      context: 'Bakterier, renter i banken, virale videoer',
      content: 'Ganging er ikke bare et mattestykke — det er oppskriften på alt som vokser fort. Bakterier dobler seg hver time. Penger i en sparekonto dobler seg med renter. En viral video kan gå fra 100 til 100 millioner seere på noen dager. Alt dette er ganging i virkeligheten.',
    },
    realWorldBridgeEn: {
      context: 'Bacteria, bank interest, viral videos',
      content: 'Multiplication is not just a maths exercise — it is the recipe for everything that grows fast. Bacteria double every hour. Money in a savings account doubles with interest. A viral video can go from 100 to 100 million viewers in a few days. All of this is multiplication in the real world.',
    },
    coreConcept: {
      definition: 'Ganging er en rask måte å legge sammen like grupper. 4 × 3 betyr "fire grupper med tre" = 3 + 3 + 3 + 3 = 12.',
      terminology: ['faktor', 'produkt', 'dobling', 'gangetabell', 'like grupper'],
      explanation: 'Når du ganger, kombinerer du like store grupper. Papirbrettingen viser dette perfekt: hver brett = ×2. 1 brett → 2 lag. 2 brett → 4 lag. 3 brett → 8 lag. Mønsteret er 1, 2, 4, 8, 16, 32... Dette kalles dobling, og det er ganging med 2 om og om igjen. Gangetabellen er en snarvei: i stedet for å legge sammen 7 + 7 + 7 + 7 + 7, sier vi bare 5 × 7 = 35.',
      workedExamples: [
        {
          problem: 'Et klasserom har 6 rader med 5 pulter i hver rad. Hvor mange pulter er det?',
          solution: '6 × 5 = 30 pulter',
          explanation: 'Vi har 6 grupper (rader), og i hver gruppe er det 5 pulter. 6 × 5 betyr "seks femmere": 5 + 5 + 5 + 5 + 5 + 5 = 30. Eller bruk gangetabellen: 6 × 5 = 30.',
        },
        {
          problem: 'Du bretter papiret 4 ganger. Hvor mange lag har du da?',
          solution: '2 × 2 × 2 × 2 = 16 lag',
          explanation: 'Hvert brett dobler lagene: 1→2→4→8→16. Det er det samme som 2⁴ = 16. Etter bare 4 brettinger har du 16 lag — det er kraften av gjentatt ganging!',
        },
      ],
    },
    coreConceptEn: {
      definition: 'Multiplication is a fast way to add equal groups. 4 × 3 means "four groups of three" = 3 + 3 + 3 + 3 = 12.',
      terminology: ['factor', 'product', 'doubling', 'times table', 'equal groups'],
      explanation: 'When you multiply, you are combining equal-sized groups. The paper folding shows this perfectly: each fold = ×2. 1 fold → 2 layers. 2 folds → 4 layers. 3 folds → 8 layers. The pattern is 1, 2, 4, 8, 16, 32... This is called doubling, and it is multiplication by 2 again and again. Times tables are a shortcut: instead of adding 7 + 7 + 7 + 7 + 7, we just say 5 × 7 = 35.',
      workedExamples: [
        {
          problem: 'A classroom has 6 rows with 5 desks in each row. How many desks are there?',
          solution: '6 × 5 = 30 desks',
          explanation: 'We have 6 groups (rows), and in each group there are 5 desks. 6 × 5 means "six fives": 5 + 5 + 5 + 5 + 5 + 5 = 30. Or use the times table: 6 × 5 = 30.',
        },
        {
          problem: 'You fold the paper 4 times. How many layers do you have?',
          solution: '2 × 2 × 2 × 2 = 16 layers',
          explanation: 'Each fold doubles the layers: 1→2→4→8→16. That is the same as 2⁴ = 16. After just 4 folds you have 16 layers — that is the power of repeated multiplication!',
        },
      ],
    },
    interactiveComponent: {
      type: 'number_input',
      description: 'Fyll inn papirbrettingstabellen: 1 brett = 2 lag, 2 brett = ___, 3 brett = ___, ...',
      phetUrl: null,
    },
    practiceQuestions: [
      {
        question: 'Du bretter papiret 3 ganger. Hvor mange lag har du?',
        type: 'multiple_choice',
        options: ['6', '8', '9', '12'],
        correctAnswer: '8',
        explanation: 'Hvert brett dobler lagene: 1 brett = 2 lag, 2 brett = 4 lag, 3 brett = 8 lag. Mønsteret: ×2 hver gang!',
        solutionSteps: ['Start med 1 lag', 'Brett 1: 1 × 2 = 2 lag', 'Brett 2: 2 × 2 = 4 lag', 'Brett 3: 4 × 2 = 8 lag ✓'],
      },
      {
        question: 'Mamma kjøpte 4 pakker med 6 blyanter i hver. Hvor mange blyanter er det?',
        type: 'multiple_choice',
        options: ['10', '20', '24', '48'],
        correctAnswer: '24',
        explanation: '4 pakker × 6 blyanter = 24 blyanter. Fire seksere: 6 + 6 + 6 + 6 = 24.',
        solutionSteps: ['4 pakker, 6 blyanter i hver', '4 × 6 = ?', '6 + 6 + 6 + 6 = 24', '4 × 6 = 24 ✓'],
      },
      {
        question: 'Hva er 7 × 8?',
        type: 'multiple_choice',
        options: ['54', '56', '58', '64'],
        correctAnswer: '56',
        explanation: '7 × 8 = 56. En fin huskeregel: 5, 6, 7, 8 → 56 = 7 × 8!',
        solutionSteps: ['7 × 8 = ?', 'Tenk: 7 × 8 er det samme som 7 × 4 × 2', '7 × 4 = 28, 28 × 2 = 56', '7 × 8 = 56 ✓'],
      },
      {
        question: 'En bils motor har 4 sylindre, og hver sylinder fyrer 3 ganger per sekund. Hvor mange ganger fyrer motoren per sekund?',
        type: 'multiple_choice',
        options: ['7', '9', '12', '16'],
        correctAnswer: '12',
        explanation: '4 sylindre × 3 ganger = 12 ganger per sekund. Ganging hjelper oss å forstå motorer!',
        solutionSteps: ['4 sylindre, 3 fyringer per sylinder per sekund', '4 × 3 = ?', '3 + 3 + 3 + 3 = 12', '4 × 3 = 12 ✓'],
      },
      {
        question: 'Etter 5 brett med papir, hvor mange lag har du?',
        type: 'multiple_choice',
        options: ['10', '16', '32', '64'],
        correctAnswer: '32',
        explanation: 'Hvert brett dobler: 2, 4, 8, 16, 32. Etter 5 brett har du 32 lag av papir!',
        solutionSteps: ['Brett 1: 2', 'Brett 2: 4', 'Brett 3: 8', 'Brett 4: 16', 'Brett 5: 32 ✓'],
      },
      {
        question: 'Et eggkartong holder 6 egg i hver rad, og det er 2 rader. Hvor mange egg er det plass til?',
        type: 'multiple_choice',
        options: ['8', '10', '12', '14'],
        correctAnswer: '12',
        explanation: '2 rader × 6 egg = 12 egg. Det er slik vi lett teller ting ordnet i rader!',
        solutionSteps: ['2 rader × 6 egg per rad', '2 × 6 = ?', '6 + 6 = 12', '2 × 6 = 12 ✓'],
      },
      {
        question: 'Hva er det dobbelte av 9?',
        type: 'multiple_choice',
        options: ['11', '16', '18', '81'],
        correctAnswer: '18',
        explanation: 'Dobbelt av 9 = 2 × 9 = 18. Dobling er bare ganging med 2!',
        solutionSteps: ['Dobbelt = × 2', '2 × 9 = ?', '9 + 9 = 18', '2 × 9 = 18 ✓'],
      },
      {
        question: 'Det er 3 poser med 8 epler i hver. Hvor mange epler er det?',
        type: 'multiple_choice',
        options: ['11', '21', '24', '38'],
        correctAnswer: '24',
        explanation: '3 poser × 8 epler = 24 epler. Tre åttere: 8 + 8 + 8 = 24.',
        solutionSteps: ['3 poser, 8 epler per pose', '3 × 8 = ?', '8 + 8 + 8 = 24', '3 × 8 = 24 ✓'],
      },
    ],
    practiceQuestionsEn: [
      {
        question: 'You fold the paper 3 times. How many layers do you have?',
        type: 'multiple_choice',
        options: ['6', '8', '9', '12'],
        correctAnswer: '8',
        explanation: 'Each fold doubles the layers: 1 fold = 2 layers, 2 folds = 4 layers, 3 folds = 8 layers. The pattern: ×2 every time!',
        solutionSteps: ['Start with 1 layer', 'Fold 1: 1 × 2 = 2 layers', 'Fold 2: 2 × 2 = 4 layers', 'Fold 3: 4 × 2 = 8 layers ✓'],
      },
      {
        question: 'Mum bought 4 packs with 6 pencils each. How many pencils are there?',
        type: 'multiple_choice',
        options: ['10', '20', '24', '48'],
        correctAnswer: '24',
        explanation: '4 packs × 6 pencils = 24 pencils. Four sixes: 6 + 6 + 6 + 6 = 24.',
        solutionSteps: ['4 packs, 6 pencils in each', '4 × 6 = ?', '6 + 6 + 6 + 6 = 24', '4 × 6 = 24 ✓'],
      },
      {
        question: 'What is 7 × 8?',
        type: 'multiple_choice',
        options: ['54', '56', '58', '64'],
        correctAnswer: '56',
        explanation: '7 × 8 = 56. A handy trick: 5, 6, 7, 8 → 56 = 7 × 8!',
        solutionSteps: ['7 × 8 = ?', 'Think: 7 × 4 × 2', '7 × 4 = 28, 28 × 2 = 56', '7 × 8 = 56 ✓'],
      },
    ],
    handsOnActivity: {
      title: 'Papirbrettingseksperiment',
      materials: ['Et A4-ark papir', 'Linjal (valgfritt)', 'Kalkulator (valgfritt — men prøv uten først!)'],
      instructions: [
        'Ta et vanlig ark papir. Dette er ditt "Månen-papir".',
        'Brett det i to. Merk "Brett 1: 2 lag" på et ark.',
        'Brett igjen. "Brett 2: 4 lag". Fortsett til du ikke kan brette mer (ca. 7 ganger).',
        'Fyll ut tabellen: Brett 1=2, Brett 2=4, Brett 3=___, Brett 4=___, Brett 5=___',
        'Kan du finne mønsteret? Hva ville Brett 10 vært? Brett 20?',
      ],
      timeMinutes: 10,
      whatToLookFor: 'Barnet bør oppdage at hvert brett dobler det forrige — og begynne å se forbindelsen mellom ganging og denne eksponentielle veksten. Spør: "Hvor mange brett for å nå taket? For å nå Månen?"',
    },
    handsOnActivityEn: {
      title: 'Paper Folding Experiment',
      materials: ['An A4 sheet of paper', 'Ruler (optional)', 'Calculator (optional — but try without first!)'],
      instructions: [
        'Take a normal sheet of paper. This is your "Moon paper".',
        'Fold it in half. Note "Fold 1: 2 layers" on a sheet.',
        'Fold again. "Fold 2: 4 layers". Continue until you cannot fold any more (about 7 times).',
        'Fill in the table: Fold 1=2, Fold 2=4, Fold 3=___, Fold 4=___, Fold 5=___',
        'Can you find the pattern? What would Fold 10 be? Fold 20?',
      ],
      timeMinutes: 10,
      whatToLookFor: 'The child should discover that each fold doubles the previous — and begin to see the connection between multiplication and exponential growth. Ask: "How many folds to reach the ceiling? To reach the Moon?"',
    },
    printableWorksheet: {
      title: 'Ganging og store tall',
      sections: [
        {
          type: 'warmup',
          title: 'Papirbrettingstabellen',
          instructions: 'Fullfør tabellen:',
          content: 'Brett 1 → 2 lag\nBrett 2 → 4 lag\nBrett 3 → ___ lag\nBrett 4 → ___ lag\nBrett 5 → ___ lag\nBrett 6 → ___ lag\nBrett 7 → ___ lag',
        },
        {
          type: 'practice',
          title: 'Gangeoppgaver',
          instructions: 'Sett ring rundt riktig svar:',
          content: '1. 6 × 7 =    a) 42   b) 48   c) 36   d) 56\n2. 8 × 9 =    a) 63   b) 72   c) 81   d) 56\n3. 4 × 8 =    a) 24   b) 28   c) 32   d) 36\n4. 5 × 7 =    a) 25   b) 30   c) 35   d) 40\n5. 3 × 9 =    a) 21   b) 24   c) 27   d) 30',
        },
        {
          type: 'challenge',
          title: 'Mondistansen',
          instructions: 'Regn ut:',
          content: 'Et papir er 0,1 mm tykt.\nEtter 10 brett: ___ mm\nEtter 20 brett: ___ mm\nEtter 30 brett: ___ mm\n(Måneavstand: 384 000 000 mm)',
        },
      ],
    },
    parentGuide: {
      whatChildShouldKnow: 'Barnet bør forstå ganging som gjentatt addisjon og begynne å huske gangetabellen opp til 10×10.',
      commonMisconceptions: 'Barn tror ofte at større tall alltid betyr vanskeligere ganging. 5 × 8 er like lett som 2 × 4 — det handler om å gjenkjenne mønsteret, ikke memorere.',
      dinnerTableQuestion: '"Hvis vi dobler middagsoppskriften til 8 personer i stedet for 4 — hva skjer med mengden av hver ingrediens?"',
      nextLessonPreview: 'Neste gang: Gangetabellen som et kart — hvordan finne snarveier og mønstre i tallene.',
    },
    parentGuideEn: {
      whatChildShouldKnow: 'The child should understand multiplication as repeated addition and begin memorising times tables up to 10×10.',
      commonMisconceptions: 'Children often think larger numbers always mean harder multiplication. 5 × 8 is just as easy as 2 × 4 — it is about recognising the pattern, not memorising.',
      dinnerTableQuestion: '"If we double the dinner recipe from 4 people to 8 — what happens to the amount of each ingredient?"',
      nextLessonPreview: 'Next time: The times table as a map — how to find shortcuts and patterns in numbers.',
    },
    openDoor: 'Du har nettopp oppdaget noe som matematikere kaller eksponentiell vekst — og det er en av de kraftigste ideene i hele matematikken. Bakterier bruker det. Bankrenter bruker det. Til og med universet bruker det (det ekspanderer eksponentielt). Neste gang ser vi nærmere på gangetabellen og finner de skjulte mønstrene som gjør det lettere å huske den.',
    openDoorEn: 'You have just discovered something mathematicians call exponential growth — and it is one of the most powerful ideas in all of mathematics. Bacteria use it. Bank interest uses it. Even the universe uses it (it expands exponentially). Next time we look more closely at the times table and find the hidden patterns that make it easier to remember.',
    curriculumSources: [
      'NCERT Math-Magic Class 3: Chapter 2 — Fun with Numbers (multiplication as repeated addition)',
      'NCERT Class 4: Chapter 3 — A Trip to Bhopal (multiplication in context)',
      'Norwegian LK20 3-4. trinn: Multiplikasjon og divisjon, gangetabellen (1-10)',
      'Finnish POPS Grades 3-4: Multiplication tables, repeated addition',
      'Common Core 3.OA.A.1 — Interpret products of whole numbers',
      'Common Core 3.OA.B.5 — Apply properties of operations as strategies to multiply',
    ],
    learningObjectives: [
      'Forstå ganging som gjentatt addisjon og like grupper',
      'Gjenkjenne doblingmønsteret og knytte det til ganging med 2',
      'Løse enkle gangeproblem i virkelige kontekster',
      'Begynne å huske gangetabellen opp til 10×10',
    ],
    learningObjectivesEn: [
      'Understand multiplication as repeated addition and equal groups',
      'Recognise the doubling pattern and connect it to multiplying by 2',
      'Solve simple multiplication problems in real-world contexts',
      'Begin memorising the times table up to 10×10',
    ],
    generatedAt: new Date(),
    version: 1,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 🚀 CHALLENGER — Lesson 1: Functions (Ages 13–16)
  // Synthesised from: NCERT Class 8-9 · Norwegian LK20 8-10.trinn
  //                   Finnish POPS 7-9 · Common Core 8.F, HSA-CED
  // ═══════════════════════════════════════════════════════════════════════════
  'functions-voyager-equation': {
    id: 'functions-voyager-equation',
    pathwayId: 'math-functions-13-16',
    subject: 'mathematics',
    strand: 'Algebra og funksjoner',
    ageBand: '13-16',
    phase: 'challenger',
    level: 1,
    language: 'no',
    title: 'Hvordan visste NASA nøyaktig hvor Voyager ville være om 46 år?',
    titleEn: 'How did NASA know exactly where Voyager would be 46 years later?',
    durationMinutes: 45,
    saganHook: {
      type: 'question',
      content: 'I 1977 skjøt NASA opp Voyager 1. Ingeniørene beregnet nøyaktig banen gjennom Jupiter, Saturn, Uranus og Neptun — og deretter ut av solsystemet for alltid. I dag er Voyager over 23 milliarder kilometer unna. NASA vet fortsatt nøyaktig hvor den er — ned til kilometeren. Ikke fordi de kan se den (det kan de ikke), men fordi en ligning forteller dem det. En ligning, skrevet i 1977, predikerer virkeligheten 46 år senere. Det er ikke magi. Det er funksjoner.',
    },
    saganHookEn: 'In 1977 NASA launched Voyager 1. Engineers calculated exactly its path through Jupiter, Saturn, Uranus and Neptune — and then out of the solar system for ever. Today Voyager is over 23 billion kilometres away. NASA still knows exactly where it is — to the kilometre. Not because they can see it (they cannot), but because an equation tells them. One equation, written in 1977, predicts reality 46 years later. That is not magic. That is functions.',
    realWorldBridge: {
      context: 'Romfart, medisin, klimamodeller, finans',
      content: 'Funksjoner er ikke symboler på et ark — de er modeller av virkeligheten. Leger bruker funksjoner til å beregne medisindoser etter kroppsvekt. Klimaforskere bruker funksjoner til å modellere CO₂-vekst. Banker bruker funksjoner til å beregne lånerenter. Og NASA bruker funksjoner til å navigere romskip milliarden av kilometer unna. Alle disse er den samme matematikken du skal lære nå.',
    },
    realWorldBridgeEn: {
      context: 'Space travel, medicine, climate models, finance',
      content: 'Functions are not symbols on a page — they are models of reality. Doctors use functions to calculate medicine doses from body weight. Climate scientists use functions to model CO₂ growth. Banks use functions to calculate loan interest. And NASA uses functions to navigate spacecraft billions of kilometres away. All of these use the same mathematics you are learning now.',
    },
    coreConcept: {
      definition: 'En funksjon er en regel som kobler en innverdi (x) til nøyaktig én utverdi (y). Skrevet som y = f(x) eller y = mx + b for lineære funksjoner.',
      terminology: ['funksjon', 'variabel', 'stigningstall', 'konstantledd', 'lineær', 'koordinatsystem', 'x-akse', 'y-akse'],
      explanation: 'En lineær funksjon y = mx + b beskriver en rett linje: m er stigningstallet (hvor fort y endrer seg per enhet x), og b er y-skjæringspunktet (verdien av y når x = 0). Voyagers avstand kan skrives som d = 17t, der d er distansen i km og t er tid i sekunder — et klassisk eksempel på en lineær funksjon uten konstantledd.',
      workedExamples: [
        {
          problem: 'Voyager beveger seg med omtrent 17 km/sek. Skriv en funksjon for avstanden d(t) etter t sekunder.',
          solution: 'd(t) = 17t',
          explanation: 'Stigningstall m = 17 (km per sekund). Ingen startavstand (b = 0). Etter 1 sekund: d = 17 km. Etter 1 år (≈ 31.557.600 sekunder): d = 17 × 31.557.600 ≈ 536 millioner km.',
        },
        {
          problem: 'En taxi tar 50 kr starttakst + 15 kr/km. Skriv funksjonen og beregn kostnaden for 12 km.',
          solution: 'f(x) = 15x + 50 → f(12) = 15(12) + 50 = 180 + 50 = 230 kr',
          explanation: 'Stigningstall m = 15 (kroner per km), konstantledd b = 50 (starttakst). Sett inn x = 12: 15 × 12 + 50 = 180 + 50 = 230 kr.',
        },
      ],
    },
    coreConceptEn: {
      definition: 'A function is a rule that connects one input value (x) to exactly one output value (y). Written as y = f(x) or y = mx + b for linear functions.',
      terminology: ['function', 'variable', 'slope', 'y-intercept', 'linear', 'coordinate system', 'x-axis', 'y-axis'],
      explanation: 'A linear function y = mx + b describes a straight line: m is the slope (how fast y changes per unit of x), and b is the y-intercept (the value of y when x = 0). Voyager\'s distance can be written as d = 17t, where d is distance in km and t is time in seconds — a classic example of a linear function with no constant term.',
      workedExamples: [
        {
          problem: 'Voyager moves at approximately 17 km/sec. Write a function for distance d(t) after t seconds.',
          solution: 'd(t) = 17t',
          explanation: 'Slope m = 17 (km per second). No starting distance (b = 0). After 1 second: d = 17 km. After 1 year (≈ 31,557,600 seconds): d = 17 × 31,557,600 ≈ 536 million km.',
        },
        {
          problem: 'A taxi charges a 50 kr start fee + 15 kr/km. Write the function and calculate the cost for 12 km.',
          solution: 'f(x) = 15x + 50 → f(12) = 15(12) + 50 = 180 + 50 = 230 kr',
          explanation: 'Slope m = 15 (kroner per km), y-intercept b = 50 (start fee). Substitute x = 12: 15 × 12 + 50 = 180 + 50 = 230 kr.',
        },
      ],
    },
    interactiveComponent: {
      type: 'slider',
      description: 'Juster stigningstall og konstantledd — se linjen flytte seg i koordinatsystemet i sanntid',
      phetUrl: 'https://phet.colorado.edu/en/simulations/graphing-lines',
    },
    practiceQuestions: [
      {
        question: 'Voyager beveger seg 17 km/sek. Hva er funksjonen for distansen d(t) etter t sekunder?',
        type: 'multiple_choice',
        options: ['d(t) = t + 17', 'd(t) = 17t', 'd(t) = 17/t', 'd(t) = t/17'],
        correctAnswer: 'd(t) = 17t',
        explanation: 'Distansen = hastighet × tid. Hastigheten er 17 km/sek (stigningstall), og vi starter fra 0 (ingen konstantledd). Funksjonen er d(t) = 17t.',
        solutionSteps: ['Distanse = hastighet × tid', 'Hastighet = 17 km/sek = stigningstallet m', 'Start fra 0 → konstantledd b = 0', 'd(t) = 17t ✓'],
      },
      {
        question: 'I funksjonen y = 4x + 7, hva er stigningstallet?',
        type: 'multiple_choice',
        options: ['7', '4', '11', 'x'],
        correctAnswer: '4',
        explanation: 'I y = mx + b er m stigningstallet. Her er m = 4. Det betyr at for hver enhet x øker, øker y med 4.',
        solutionSteps: ['y = mx + b er malen', 'Sammenlign med y = 4x + 7', 'm = 4 (koeffisienten foran x)', 'Stigningstall = 4 ✓'],
      },
      {
        question: 'En telefonabonnement koster 199 kr/måned + 2 kr per ekstra SMS. Du sender 40 SMS. Hva koster måneden?',
        type: 'multiple_choice',
        options: ['239 kr', '279 kr', '241 kr', '199 kr'],
        correctAnswer: '279 kr',
        explanation: 'Funksjon: f(x) = 2x + 199. Sett inn x = 40: f(40) = 2(40) + 199 = 80 + 199 = 279 kr.',
        solutionSteps: ['Funksjon: f(x) = 2x + 199', 'x = 40 SMS', 'f(40) = 2 × 40 + 199', '= 80 + 199 = 279 kr ✓'],
      },
      {
        question: 'Hva er y-skjæringspunktet i funksjonen y = 3x − 5?',
        type: 'multiple_choice',
        options: ['3', '−5', '5', '−3'],
        correctAnswer: '−5',
        explanation: 'y-skjæringspunktet er b i y = mx + b — verdien av y når x = 0. Her: y = 3(0) − 5 = −5.',
        solutionSteps: ['y-skjæringspunkt: sett x = 0', 'y = 3(0) − 5', 'y = 0 − 5 = −5', 'y-skjæringspunkt = −5 ✓'],
      },
      {
        question: 'Punktene (0, 2), (1, 5), (2, 8) ligger på en linje. Hva er funksjonsregelen?',
        type: 'multiple_choice',
        options: ['y = 2x + 3', 'y = 3x + 2', 'y = 5x', 'y = x + 2'],
        correctAnswer: 'y = 3x + 2',
        explanation: 'Fra (0,2) ser vi b = 2. Fra (0,2) til (1,5): y øker med 3 når x øker med 1, så m = 3. Funksjonen: y = 3x + 2.',
        solutionSteps: ['Når x=0, y=2 → b = 2', 'Fra x=0 til x=1: y går fra 2 til 5 → stigning = 3', 'm = 3', 'y = 3x + 2 ✓'],
      },
      {
        question: 'Er funksjonen f(x) = x² lineær?',
        type: 'multiple_choice',
        options: ['Ja, fordi den har en variabel', 'Nei, fordi x er opphøyd i andre', 'Ja, fordi den er en funksjon', 'Nei, fordi den ikke har en b-verdi'],
        correctAnswer: 'Nei, fordi x er opphøyd i andre',
        explanation: 'En lineær funksjon er alltid y = mx + b — x er bare i første potens. Når x² er med, er grafen en parabel, ikke en rett linje.',
        solutionSteps: ['Lineær funksjon: y = mx + b', 'x² betyr x er i andre potens', 'Det gir en kurve (parabel), ikke en linje', 'f(x) = x² er IKKE lineær ✓'],
      },
      {
        question: 'Voyager ble skutt opp i 1977. Bruk d(t) = 17t (km/sek). Omtrent hvor langt har den reist på 1 år (ca. 31,5 millioner sekunder)?',
        type: 'multiple_choice',
        options: ['17 000 km', '535 000 000 km', '17 000 000 km', '5 350 km'],
        correctAnswer: '535 000 000 km',
        explanation: 'd = 17 × 31 500 000 ≈ 535 500 000 km. Omtrent 535 millioner km på ett år — mer enn avstanden fra Jorda til Jupiter!',
        solutionSteps: ['d(t) = 17t', 't = 31 500 000 sekunder (1 år)', 'd = 17 × 31 500 000', '= 535 500 000 km ≈ 535 millioner km ✓'],
      },
    ],
    practiceQuestionsEn: [
      {
        question: 'Voyager travels at 17 km/sec. What is the function for distance d(t) after t seconds?',
        type: 'multiple_choice',
        options: ['d(t) = t + 17', 'd(t) = 17t', 'd(t) = 17/t', 'd(t) = t/17'],
        correctAnswer: 'd(t) = 17t',
        explanation: 'Distance = speed × time. Speed is 17 km/sec (the slope), and we start from 0 (no constant term). The function is d(t) = 17t.',
        solutionSteps: ['Distance = speed × time', 'Speed = 17 km/sec = slope m', 'Start from 0 → constant b = 0', 'd(t) = 17t ✓'],
      },
      {
        question: 'In the function y = 4x + 7, what is the slope?',
        type: 'multiple_choice',
        options: ['7', '4', '11', 'x'],
        correctAnswer: '4',
        explanation: 'In y = mx + b, m is the slope. Here m = 4. This means for every unit increase in x, y increases by 4.',
        solutionSteps: ['y = mx + b is the template', 'Compare with y = 4x + 7', 'm = 4 (coefficient in front of x)', 'Slope = 4 ✓'],
      },
      {
        question: 'A phone plan costs 199 kr/month + 2 kr per extra SMS. You send 40 SMS. What does the month cost?',
        type: 'multiple_choice',
        options: ['239 kr', '279 kr', '241 kr', '199 kr'],
        correctAnswer: '279 kr',
        explanation: 'Function: f(x) = 2x + 199. Substitute x = 40: f(40) = 2(40) + 199 = 80 + 199 = 279 kr.',
        solutionSteps: ['Function: f(x) = 2x + 199', 'x = 40 SMS', 'f(40) = 2 × 40 + 199', '= 80 + 199 = 279 kr ✓'],
      },
    ],
    handsOnActivity: {
      title: 'Voyager-kalkulator: Hvor er romskipet nå?',
      materials: ['Kalkulator', 'Papir og blyant', 'Koordinatpapir (valgfritt)'],
      instructions: [
        'NASA Voyager 1 ble skutt opp 5. september 1977. Beregn antall dager siden oppskytning.',
        'Gjør om til sekunder: dager × 24 × 3600.',
        'Bruk funksjonen d(t) = 17t for å beregne Voyagers omtrentlige avstand fra Jorda.',
        'Sammenlign med den faktiske avstanden på voyager.jpl.nasa.gov',
        'Tegn funksjonen på koordinatpapir: x-aksen = tid (år), y-aksen = avstand (milliarder km).',
      ],
      timeMinutes: 20,
      whatToLookFor: 'Eleven bør se at en enkel lineær funksjon faktisk predikerer virkeligheten med bemerkelsesverdig nøyaktighet. Diskuter: "Hvorfor er ikke funksjonen 100% nøyaktig? Hva er forenklingene?"',
    },
    handsOnActivityEn: {
      title: 'Voyager Calculator: Where Is the Spacecraft Now?',
      materials: ['Calculator', 'Paper and pencil', 'Graph paper (optional)'],
      instructions: [
        'NASA Voyager 1 was launched on 5 September 1977. Calculate the number of days since launch.',
        'Convert to seconds: days × 24 × 3600.',
        'Use the function d(t) = 17t to calculate Voyager\'s approximate distance from Earth.',
        'Compare with the actual distance at voyager.jpl.nasa.gov',
        'Plot the function on graph paper: x-axis = time (years), y-axis = distance (billion km).',
      ],
      timeMinutes: 20,
      whatToLookFor: 'The student should see that a simple linear function actually predicts reality with remarkable accuracy. Discuss: "Why is the function not 100% accurate? What are the simplifications?"',
    },
    printableWorksheet: {
      title: 'Lineære funksjoner og Voyager',
      sections: [
        {
          type: 'warmup',
          title: 'Funksjonsnotasjon',
          instructions: 'Fyll inn verdiene:',
          content: 'f(x) = 3x + 2\nf(0) = ___\nf(1) = ___\nf(5) = ___\nf(10) = ___',
        },
        {
          type: 'practice',
          title: 'Identifiser stigningstall og konstantledd',
          instructions: 'For hver funksjon: skriv m (stigningstall) og b (konstantledd):',
          content: '1. y = 5x + 3    → m=___, b=___\n2. y = −2x + 8   → m=___, b=___\n3. y = 7x         → m=___, b=___\n4. f(x) = x − 4  → m=___, b=___',
        },
        {
          type: 'challenge',
          title: 'Skriv din egen funksjon',
          instructions: 'Du tjener 150 kr per time på en sommerjobb. Skriv en funksjon for total inntekt I(t) etter t timer. Hva tjener du etter en hel dag (8 timer)?',
          content: 'Funksjon: I(t) = ___\nEtter 8 timer: I(8) = ___ kr',
        },
        {
          type: 'reflection',
          title: 'Refleksjon',
          instructions: 'Svar med egne ord:',
          content: 'Forklar med et eksempel fra virkeligheten hva et stigningstall betyr. Hvorfor er dette nyttig å vite?',
        },
      ],
    },
    parentGuide: {
      whatChildShouldKnow: 'Eleven bør forstå hva en lineær funksjon er, identifisere stigningstall og konstantledd i y=mx+b, og bruke funksjoner til å løse virkelige problemer.',
      commonMisconceptions: 'Mange elever tror "funksjon" bare er et annet ord for "formel". Poenget er at en funksjon er en deterministisk regel — én x-verdi gir alltid nøyaktig én y-verdi. Dette skiller det fra et utrykk.',
      dinnerTableQuestion: '"Hvis du ville beskrive kostnadene dine denne måneden som en funksjon — hva ville stigningstallet og konstantleddet ditt være?"',
      nextLessonPreview: 'Neste gang: Hva betyr det at en funksjon er lineær vs. eksponentiell — og hvorfor dette skillet er avgjørende for å forstå klimaendringer.',
    },
    parentGuideEn: {
      whatChildShouldKnow: 'The student should understand what a linear function is, identify slope and y-intercept in y=mx+b, and use functions to solve real-world problems.',
      commonMisconceptions: 'Many students think "function" is just another word for "formula". The point is that a function is a deterministic rule — one x-value always gives exactly one y-value. This distinguishes it from an expression.',
      dinnerTableQuestion: '"If you wanted to describe your expenses this month as a function — what would your slope and y-intercept be?"',
      nextLessonPreview: 'Next time: What does it mean for a function to be linear vs. exponential — and why this distinction is crucial for understanding climate change.',
    },
    openDoor: 'Du kan nå lese og skrive lineære funksjoner — det samme verktøyet NASA bruker til å navigere romskip. Men hva skjer når veksten ikke er jevn? Hva hvis noe dobler seg, ikke bare øker med en fast mengde? Det er eksponentielle funksjoner — og de forklarer alt fra COVID-spredning til rentes rente til universets ekspansjon. Det er neste leksjon.',
    openDoorEn: 'You can now read and write linear functions — the same tool NASA uses to navigate spacecraft. But what happens when growth is not steady? What if something doubles, rather than just increasing by a fixed amount? That is exponential functions — and they explain everything from COVID spread to compound interest to the expansion of the universe. That is the next lesson.',
    curriculumSources: [
      'NCERT Class 8: Chapter 2 — Linear Equations in One Variable',
      'NCERT Class 9: Chapter 3 — Coordinate Geometry; Chapter 4 — Linear Equations in Two Variables',
      'Norwegian LK20 8-10. trinn: Algebra — funksjoner, stigningstall, lineære funksjoner',
      'Finnish POPS Grades 7-9: Linear functions, coordinate system, equations',
      'Common Core 8.F.A.1 — Understand that a function assigns each input exactly one output',
      'Common Core 8.F.B.4 — Construct a function to model a linear relationship',
      'Common Core HSA-CED.A.1 — Create equations in one or two variables',
    ],
    learningObjectives: [
      'Forstå hva en funksjon er: én x-verdi → nøyaktig én y-verdi',
      'Identifisere og tolke stigningstall og konstantledd i y = mx + b',
      'Lage og bruke lineære funksjoner til å modellere virkelige situasjoner',
      'Tegne lineære funksjoner i koordinatsystemet',
    ],
    learningObjectivesEn: [
      'Understand what a function is: one x-value → exactly one y-value',
      'Identify and interpret slope and y-intercept in y = mx + b',
      'Create and use linear functions to model real-world situations',
      'Graph linear functions in the coordinate system',
    ],
    generatedAt: new Date(),
    version: 1,
  },
};
